import React, {
  useMemo,
  useEffect,
  useLayoutEffect,
  useCallback,
  useState,
  useRef,
} from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector, useStore } from "react-redux";
import "./Quote.scss";
import Modal from "../../common/components/Modal";
import HoverTooltip from "../../common/components/HoverTooltip";
import QuickPeep from "./Peep";
import BrandLoader from "../../common/components/BrandLoader";
import EditStepModal from "../../common/components/EditStepModal";
import { BackArrowIcon, InfoCircleIcon } from "../../common/components/Icons";
import { getPincodeMasterRecord } from "../../api/formService";
import { submitQuote, initQuote } from "../../api/quoteService";
import { resolveBiApiProductId } from "../../api/paymentService";
import { getPremiumResultForDimensions } from "../../api/premiumService";
import { readTermPlanPincodeAvailability } from "../../common/utils/pincodeServiceability";
import { logError } from "../../common/utils/logger";

import {
  formatCurrency,
  formatLakhs,
  parseCoverAmountInput,
  quantizeCoverAmountToRupeesStep,
  sanitizeCoverAmountInputDecimals,
  calculateAge,
  formatDateDDMMYYYY,
} from "../../common/utils/formatters";
import { RIDER_ID_ADD_PLUS } from "../../common/utils/riderCoverRules";
import {
  buildPremiumContextKey,
  getContextAwareMultiResult,
  getContextAwareMultiResultStrict,
} from "../../common/utils/premiumContextKey";
import {
  CUSTOM_COVER_PREMIUM_DEBOUNCE_MS,
  RIDER_COVER_PREMIUM_DEBOUNCE_MS,
  MIN_COVER_ELITE,
  MIN_COVER_PLUS,
  MAX_COVER_TILL_AGE_ABSOLUTE,
  MAX_COVER_TILL_AGE_PLUS,
  MAX_POLICY_TERM_YEARS,
  MIN_POLICY_TERM_YEARS,
  PLAN_NAMES,
  PLAN_PRODUCT_CODES,
  getDefaultCoverAmount,
  getLimitedPayOptionsForPlan,
  TEXT_CONTENT,
} from "../../constants";
import {
  buildCoverTillPolicyTermPairs,
  effectiveMaxCoverTillAgeForPlan,
  effectiveMaxPolicyTermForPlan,
  reconciledMaxPolicyTermForCoverTillGrid,
} from "../../common/utils/planTermCaps";
import { useJourney } from "../../common/hooks/useJourney";
import useQuoteState from "./useQuoteState";
import { useQuoteRiders } from "./useQuoteRiders";
import QuoteRidersSection from "./QuoteRidersSection";
import {
  PLAN_DISPLAY_NAMES,
  PLAN_TO_PRODUCT_CODE,
  resolveVisiblePlanFromSavedQuote,
} from "./constants/planLabels";
import {
  BI_RIDER_ID_BY_FRONTEND_ID,
  BI_RIDER_NAME_BY_FRONTEND_ID,
  RIDERS_DATA,
} from "./constants/riderConstants";
import { cloneForJourney } from "./utils/quoteClone";
import { debugLog } from "./utils/quoteDebug";
import { getRelevantPremiumErrorMessage } from "./utils/quotePremiumMessages";
import {
  generateCoverAmountOptions,
  getDefaultSelectedCoverAmount,
} from "./utils/coverAmountOptions";
import {
  getDisplayFrequencyLabel,
  getInputModeFromFrequency,
} from "./utils/premiumFrequency";
import {
  ECBO_MAX_AGE_EXCLUSIVE,
  getEcboToggleDisabledReason,
} from "./utils/ecboRules";
import { journeyActions } from "../../store/journeySlice";
import NeedhelpModal from "../../common/components/NeedhelpModal";
import { redirectToLatestJourneyStage } from "../../common/utils/journeyStageGuard";
import {
  getLeadNationality,
  getLeadStateForBi,
} from "../../common/utils/journeyLeadFields";
/** Compare resolved rider SAs vs committed state (all selected riders incl. ADD+ / CI / ADB / ADD). */
const effectiveRiderCoversDiffer = (
  resolvedCovers,
  committedCovers,
  selectedIds,
) => {
  if (!resolvedCovers || !selectedIds?.length) return false;
  for (const id of selectedIds) {
    if (Number(resolvedCovers[id]) !== Number(committedCovers?.[id])) {
      return true;
    }
  }
  return false;
};

const BI_FAILURE_MESSAGE =
  "Your Benefit Illustration could not be generated. Please try modifying the selected combination and try again";
const GUIDED_QUOTE_STEPS = {
  PLAN: "plan",
  FREQUENCY: "frequency",
  LIFE_COVER: "lifeCover",
  COVER_TILL_AGE: "coverTillAge",
  PAYMENT_TERM: "paymentTerm",
};
const GUIDED_STEP_ORDER = [
  GUIDED_QUOTE_STEPS.PLAN,
  GUIDED_QUOTE_STEPS.FREQUENCY,
  GUIDED_QUOTE_STEPS.LIFE_COVER,
  GUIDED_QUOTE_STEPS.COVER_TILL_AGE,
  GUIDED_QUOTE_STEPS.PAYMENT_TERM,
];
const GUIDED_STEPS_WITH_BULK_API = [
  GUIDED_QUOTE_STEPS.LIFE_COVER,
  GUIDED_QUOTE_STEPS.COVER_TILL_AGE,
  GUIDED_QUOTE_STEPS.PAYMENT_TERM,
];
const GUIDED_STEP_TITLES = {
  [GUIDED_QUOTE_STEPS.PLAN]: "Choose the plan that best suits your needs",
  [GUIDED_QUOTE_STEPS.FREQUENCY]:
    "Choose how often you'd like to pay your premium",
  [GUIDED_QUOTE_STEPS.LIFE_COVER]:
    "Select the life cover amount you want to provide for your family",
  [GUIDED_QUOTE_STEPS.COVER_TILL_AGE]:
    "Choose the age until which you'd like to secure your family's future",
  [GUIDED_QUOTE_STEPS.PAYMENT_TERM]:
    "Choose between flexible and fixed premium payment terms",
};
const PREMIUM_FREQUENCY_PLACEHOLDER_VALUE = "";
const PREMIUM_FREQUENCY_OPTIONS = [
  "Monthly",
  "Quarterly",
  "Half-yearly",
  "Yearly",
];
const isValidPremiumFrequency = (value) =>
  PREMIUM_FREQUENCY_OPTIONS.includes(String(value || "").trim());

const GuidedHintArrow = ({ direction = "down" }) => (
  <span
    className={`quote-guided-hint__arrow quote-guided-hint__arrow--${direction}`}
    aria-hidden
  >
    <svg
      width="28"
      height="28"
      viewBox="0 0 28 28"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <g clip-path="url(#clip0_37657_17297)">
        <path
          fill-rule="evenodd"
          clip-rule="evenodd"
          d="M13.2938 0.291834C13.3867 0.198708 13.497 0.124822 13.6185 0.0744094C13.74 0.0239965 13.8703 -0.00195313 14.0018 -0.00195312C14.1333 -0.00195313 14.2636 0.0239965 14.3851 0.0744094C14.5065 0.124822 14.6169 0.198708 14.7098 0.291834L21.7098 7.29183C21.85 7.43169 21.9455 7.61004 21.9843 7.80425C22.023 7.99847 22.0032 8.19981 21.9274 8.38276C21.8515 8.56571 21.7231 8.72202 21.5583 8.83187C21.3935 8.94173 21.1998 9.00019 21.0018 8.99983H16.0018V25.9998C16.0018 26.5303 15.7911 27.039 15.416 27.414C15.0409 27.7891 14.5322 27.9998 14.0018 27.9998C13.4714 27.9998 12.9626 27.7891 12.5876 27.414C12.2125 27.039 12.0018 26.5303 12.0018 25.9998V8.99983H7.00179C6.80375 9.00019 6.61006 8.94173 6.44528 8.83187C6.2805 8.72202 6.15206 8.56571 6.07622 8.38276C6.00039 8.19981 5.98058 7.99847 6.01931 7.80425C6.05805 7.61004 6.15358 7.43169 6.29379 7.29183L13.2938 0.291834Z"
          fill="white"
        />
      </g>
      <defs>
        <clipPath id="clip0_37657_17297">
          <rect width="28" height="28" fill="white" />
        </clipPath>
      </defs>
    </svg>
  </span>
);

const TermInsuranceJourneyQuote = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const store = useStore();
  const {
    effectiveLeadResult: leadResult,
    premiumResult,
    premiumLoading,
    premiumError,
    handleBackToLead: _onBack,
    handleQuoteComplete: onComplete,
    applicationNumber,
    caseId,
    multiDimensionPremiumResult,
    multiDimensionByPlan,
    handlePremiumFrequencyChange: onPremiumFrequencyChange,
    handlePaymentTermChange: onPaymentTermChange,
    handlePlanChange: onPlanChange,
    handleCoverAmountChange: onCoverAmountChange,
    handleQuoteDimensionsChange: onQuoteDimensionsChange,
    handleRiderChange: onRiderChange,
  } = useJourney();

  const hasUserEditedQuoteRef = useRef(false);
  const markUserEditedQuote = useCallback(() => {
    hasUserEditedQuoteRef.current = true;
  }, []);
  /** True for one render cycle after handlePlanSelect, so downstream useEffects skip redundant API calls. Cleared by a trailing useEffect. */
  const planChangeInFlightRef = useRef(false);
  /** Detect Plus → Elite to lift cover-till-age off Plus 75 ceiling when higher Elite options exist. */
  const prevSelectedPlanForCoverTillRef = useRef(null);

  /** True while "Choose Desired" life-cover input is focused — avoids auto-reselecting max radio + bulk loader on focus. */
  const [
    isCustomDesiredCoverFieldFocused,
    setIsCustomDesiredCoverFieldFocused,
  ] = useState(false);

  const [showEditDetailsModal, setShowEditDetailsModal] = useState(false);
  const [showBiFailureModal, setShowBiFailureModal] = useState(false);
  const [biFailureMessage, setBiFailureMessage] = useState("");
  /** Applied once when we have lead/plan context so ROP and ECBO default to NO on landing (Scenarios 1–3). */
  const hasAppliedLandingDefaultsRef = useRef(false);
  const { leadFormData } = useSelector((s) => s.journey);
  const quoteInitResultFromStore = useSelector(
    (s) => s.journey.quoteInitResult,
  );
  const quoteRidersRedux = useSelector((s) => s.journey.quoteRiders);
  const quoteOptionsRedux = useSelector((s) => s.journey.quoteOptions);
  const quoteCoverAmountRedux = useSelector((s) => s.journey.quoteCoverAmount);
  const quoteCustomCoverAmountRedux = useSelector(
    (s) => s.journey.quoteCustomCoverAmount,
  );
  const quoteCoverTillAgeRedux = useSelector(
    (s) => s.journey.quoteCoverTillAge,
  );
  const quoteCustomCoverTillAgeRedux = useSelector(
    (s) => s.journey.quoteCustomCoverTillAge,
  );
  const quoteCoverSelection = useSelector(
    (state) => state.journey.quoteResult?.quote?.coverAmount,
  );
  const quoteResultFromStore = useSelector((s) => s.journey.quoteResult);
  const journeyStages = useSelector((s) => s.journey?.journeyData?.stages);
  const quoteStageDataFromJourney = useSelector(
    (s) =>
      s.journey?.journeyData?.currentStageData?.["Stage 3.0 Quote"] ??
      s.journey?.journeyData?.stages?.["Stage 3.0 Quote"],
  );
  const quotePremiumFrequencyRedux = useSelector(
    (s) => s.journey.quotePremiumFrequency,
  );
  const quotePaymentTermTypeRedux = useSelector(
    (s) => s.journey.quotePaymentTermType,
  );
  const quoteSelectedLimitedPayTermRedux = useSelector(
    (s) => s.journey.quoteSelectedLimitedPayTerm,
  );
  const isLeadToQuoteFirstLanding =
    quoteStageDataFromJourney == null && quoteResultFromStore == null;
  const [guidedSelections, setGuidedSelections] = useState({
    [GUIDED_QUOTE_STEPS.PLAN]: false,
    [GUIDED_QUOTE_STEPS.FREQUENCY]: false,
    [GUIDED_QUOTE_STEPS.LIFE_COVER]: false,
    [GUIDED_QUOTE_STEPS.COVER_TILL_AGE]: false,
    [GUIDED_QUOTE_STEPS.PAYMENT_TERM]: false,
  });
  const [guidedStepIndex, setGuidedStepIndex] = useState(
    isLeadToQuoteFirstLanding ? 1 : 0,
  );
  const [isGuidedFlowActive, setIsGuidedFlowActive] = useState(
    isLeadToQuoteFirstLanding,
  );
  const hasInitializedGuidedFlowRef = useRef(false);
  const guidedBulkTriggeredForStepsRef = useRef(new Set());
  const [guidedBulkLoading, setGuidedBulkLoading] = useState(false);
  const isLeadGuidedFlowActive =
    isLeadToQuoteFirstLanding && isGuidedFlowActive;
  const activeGuidedStep =
    guidedStepIndex > 0 ? GUIDED_STEP_ORDER[guidedStepIndex - 1] : null;
  const guidedHintText = activeGuidedStep
    ? GUIDED_STEP_TITLES[activeGuidedStep]
    : "";
  const [guidedHintCoords, setGuidedHintCoords] = useState(null);
  const [guidedHintPlaceAbove, setGuidedHintPlaceAbove] = useState(false);

  const [quoteInitLoading, setQuoteInitLoading] = useState(false);
  const shouldFetchQuoteInit =
    !quoteInitResultFromStore ||
    quoteInitResultFromStore.policyTermOptions == null ||
    quoteInitResultFromStore.coverTillAgeOptions == null;
  const markGuidedStepComplete = useCallback(
    (stepKey) => {
      if (!isLeadGuidedFlowActive) return;
      setGuidedSelections((prev) => {
        if (prev[stepKey]) return prev;
        return { ...prev, [stepKey]: true };
      });
    },
    [isLeadGuidedFlowActive],
  );

  useEffect(() => {
    if (!shouldFetchQuoteInit) return;
    if (!leadFormData || Object.keys(leadFormData).length === 0) return;
    if (!applicationNumber || !caseId) return;
    setQuoteInitLoading(true);
    initQuote(leadFormData, applicationNumber, caseId)
      .then((resp) => {
        if (resp && typeof resp === "object") {
          dispatch(
            journeyActions.setQuoteInitResult({
              ...resp,
              ...(applicationNumber ? { applicationNumber } : {}),
            }),
          );
        }
      })
      .catch((err) => {
        logError("[Quote] initQuote resume failed", err);
      })
      .finally(() => setQuoteInitLoading(false));
  }, [shouldFetchQuoteInit, leadFormData, applicationNumber, caseId, dispatch]);

  const initialRiderCovers = useMemo(() => {
    const covers = {};
    RIDERS_DATA.forEach((rider) => {
      covers[rider.id] = rider.minCover;
    });
    return covers;
  }, []);

  const {
    selectedPlan,
    setSelectedPlan,
    selectedCoverAmount,
    setSelectedCoverAmount,
    customCoverAmount,
    setCustomCoverAmount,
    coverAmountError,
    setCoverAmountError,
    selectedCoverTillAge,
    setSelectedCoverTillAge,
    customCoverTillAge,
    setCustomCoverTillAge,
    coverTillAgeError,
    setCoverTillAgeError,
    selectedPolicyTerm,
    setSelectedPolicyTerm,
    showHelpModal,
    setShowHelpModal,
    showPlanIneligibleModal,
    setShowPlanIneligibleModal,
    showPremiumRefundModal,
    setShowPremiumRefundModal,
    showEnhancedCoverageModal,
    setShowEnhancedCoverageModal,
    premiumRefundEnabled,
    setPremiumRefundEnabled,
    enhancedCoverageEnabled,
    setEnhancedCoverageEnabled,
    applyLandingDefaults,
    switchedToPlusDueToROP,
    setSwitchedToPlusDueToROP,
    paymentTermType,
    setPaymentTermType,
    premiumFrequency,
    setPremiumFrequency,
    selectedLimitedPayTerm,
    setSelectedLimitedPayTerm,
    showMoreRiders,
    setShowMoreRiders: _setShowMoreRiders,
    selectedRiders,
    riderCovers,
    submitting,
    setSubmitting,
    submitError,
    setSubmitError,
    showSuccessModal,
    setShowSuccessModal,
    toggleRider,
    handleRiderCoverChange,
    syncRiderCovers,
    setRidersFromRedux,
  } = useQuoteState(initialRiderCovers);

  useEffect(() => {
    if (!isLeadToQuoteFirstLanding) {
      setIsGuidedFlowActive(false);
      setGuidedStepIndex(0);
      return;
    }
    setIsGuidedFlowActive(true);
  }, [isLeadToQuoteFirstLanding]);

  useEffect(() => {
    if (!isLeadGuidedFlowActive) return;
    if (!leadResult) return;
    if (hasInitializedGuidedFlowRef.current) return;
    hasInitializedGuidedFlowRef.current = true;
    guidedBulkTriggeredForStepsRef.current = new Set();
    setGuidedBulkLoading(false);
    setGuidedSelections({
      [GUIDED_QUOTE_STEPS.PLAN]: false,
      [GUIDED_QUOTE_STEPS.FREQUENCY]: false,
      [GUIDED_QUOTE_STEPS.LIFE_COVER]: false,
      [GUIDED_QUOTE_STEPS.COVER_TILL_AGE]: false,
      [GUIDED_QUOTE_STEPS.PAYMENT_TERM]: false,
    });
    setGuidedStepIndex(1);
    setSelectedPlan(null);
    setSelectedCoverAmount(null);
    setCustomCoverAmount("");
    setSelectedCoverTillAge(null);
    setCustomCoverTillAge("");
    setPremiumFrequency(PREMIUM_FREQUENCY_PLACEHOLDER_VALUE);
    setPaymentTermType(null);
    setSelectedLimitedPayTerm(null);
    dispatch(
      journeyActions.setQuoteCoverAmountSelection({
        selectedCoverAmount: null,
        customCoverAmount: "",
      }),
    );
    dispatch(
      journeyActions.setQuoteCoverTillAgeSelection({
        selectedCoverTillAge: null,
        customCoverTillAge: "",
      }),
    );
  }, [
    isLeadGuidedFlowActive,
    leadResult,
    dispatch,
    setSelectedPlan,
    setSelectedCoverAmount,
    setCustomCoverAmount,
    setSelectedCoverTillAge,
    setCustomCoverTillAge,
    setPremiumFrequency,
    setPaymentTermType,
    setSelectedLimitedPayTerm,
  ]);

  const leadInput = leadResult?.leadInput || {};
  const assignedProductName = leadResult?.assignedProductName || "";
  const isEliteEligible = assignedProductName.toLowerCase().includes("elite");
  const pincodeTermPlanAvailability = useSelector(
    (s) => s.journey?.pincodeTermPlanAvailability,
  );
  /**
   * `pincodeTermPlanAvailability` is set by Lead.jsx after the pincode-master lookup and
   * persisted via redux-persist. On a hard refresh of /quote it can be missing from the
   * rehydrated state (the journey-resume path restores leadResult / quoteResult etc. from
   * backend but does not re-evaluate the client-side pincode check). When the value is null
   * *and* we already have a leadResult (i.e. we are resuming the journey), default Elite to
   * "not allowed" — Plus is the universal base plan, so its fallback stays "allowed". This
   * keeps the visible-plans list aligned with the initial flow until the self-healing
   * useEffect below re-fetches the pincode-master availability.
   */
  const pincodeAvailabilityKnown = pincodeTermPlanAvailability != null;
  const hasLeadResult = !!leadResult;
  const pincodeAllowsPlus = pincodeAvailabilityKnown
    ? pincodeTermPlanAvailability.plusAllowed !== false
    : true;
  const pincodeAllowsElite = pincodeAvailabilityKnown
    ? pincodeTermPlanAvailability.eliteAllowed !== false
    : !hasLeadResult;

  /**
   * Self-heal pincode availability on resume / hard refresh. Runs only when the
   * Redux slot is null but we have a lead pincode to look up. The pincode master
   * is pre-loaded on app start and served from an in-memory cache, so this is
   * effectively instantaneous when the cache is warm. Result mirrors what Lead.jsx
   * dispatches on the initial flow, so the visible-plans list on refresh ends up
   * identical to what the user originally saw after lead submission.
   */
  useEffect(() => {
    if (pincodeTermPlanAvailability != null) return;
    const pincode = leadInput?.pinCode;
    if (!pincode) return;
    let cancelled = false;
    (async () => {
      try {
        const record = await getPincodeMasterRecord(pincode);
        if (cancelled || !record) return;
        const availability = readTermPlanPincodeAvailability(record);
        dispatch(journeyActions.setPincodeTermPlanAvailability(availability));
      } catch (err) {
        logError("[Quote] Restore pincode term-plan availability failed", err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [pincodeTermPlanAvailability, leadInput?.pinCode, dispatch]);

  /** When API sends max SA, require ≥ Elite minimum (₹2 Cr) to offer Elite. */
  const numericLeadMaxCover = useMemo(() => {
    const raw = leadResult?.maxCoverAmount;
    const n = Number(raw);
    if (raw != null && raw !== "" && Number.isFinite(n) && n > 0) {
      return n;
    }
    return null;
  }, [leadResult?.maxCoverAmount]);

  const eliteMeetsMinimumSumAssured = useMemo(() => {
    if (numericLeadMaxCover == null) return true;
    return numericLeadMaxCover >= MIN_COVER_ELITE;
  }, [numericLeadMaxCover]);

  /** Eligibility rules + pincode + max eligible SA for Elite floor (₹2 Cr). */
  const canOfferEliteAtPincode =
    isEliteEligible && pincodeAllowsElite && eliteMeetsMinimumSumAssured;

  const visibleTermPlans = useMemo(() => {
    const list = [];
    if (pincodeAllowsPlus) list.push(PLAN_NAMES.PLUS);
    if (canOfferEliteAtPincode) list.push(PLAN_NAMES.ELITE);
    return list;
  }, [pincodeAllowsPlus, canOfferEliteAtPincode]);

  useEffect(() => {
    if (!isLeadGuidedFlowActive) return;
    if (visibleTermPlans.length !== 1) return;
    const onlyPlan = visibleTermPlans[0];
    setSelectedPlan(onlyPlan);
  }, [isLeadGuidedFlowActive, visibleTermPlans, setSelectedPlan]);

  const showEnhancedCoverageSection = visibleTermPlans.length > 0;

  // Debug: log ROP/ECBO state and any API defaults (remove after debugging)
  debugLog("render", {
    premiumRefundEnabled,
    enhancedCoverageEnabled,
    leadResultExists: !!leadResult,
    premiumDefaultsFromApi: leadResult?.premiumDefaults
      ? {
          returnOfPremium: leadResult.premiumDefaults.returnOfPremium,
          enhancedCoverageBenefit:
            leadResult.premiumDefaults.enhancedCoverageBenefit,
        }
      : null,
  });
  const leadData = useSelector((state) => state.journey?.leadFormData);
  const journeyRehydrated = useSelector((state) => state._persist?.rehydrated);
  const editDetailsFormData = useMemo(() => {
    const stage10 = journeyStages?.["Stage 1.0 Lead"] ?? {};
    const stage11 = journeyStages?.["Stage 1.1 Lead"] ?? {};
    const stage12 = journeyStages?.["Stage 1.2 Lead"] ?? {};
    const stage20 = journeyStages?.["Stage 2.0 Lead"] ?? {};
    const leadInputFromQuote =
      quoteStageDataFromJourney?.leadInput ??
      quoteResultFromStore?.leadInput ??
      {};

    const merged = {
      ...(stage10 && typeof stage10 === "object" ? stage10 : {}),
      ...(stage11 && typeof stage11 === "object" ? stage11 : {}),
      ...(stage12 && typeof stage12 === "object" ? stage12 : {}),
      ...(stage20 && typeof stage20 === "object" ? stage20 : {}),
      ...(leadFormData && typeof leadFormData === "object" ? leadFormData : {}),
    };

    if (!merged.fullName && leadInputFromQuote?.lifeAssuredName) {
      merged.fullName = leadInputFromQuote.lifeAssuredName;
    }
    if (!merged.dateOfBirth && leadInputFromQuote?.lifeAssuredDob) {
      merged.dateOfBirth = leadInputFromQuote.lifeAssuredDob;
    }
    if (!merged.emailAddress && leadInputFromQuote?.emailId) {
      merged.emailAddress = leadInputFromQuote.emailId;
    }
    if (!merged.residencyPinCode && leadInputFromQuote?.pinCode) {
      merged.residencyPinCode = leadInputFromQuote.pinCode;
    }
    if (!merged.mobileNumber && leadInputFromQuote?.mobileNumber) {
      merged.mobileNumber = leadInputFromQuote.mobileNumber;
    }
    if (!merged.gender && leadInputFromQuote?.lifeAssuredGender) {
      merged.gender = leadInputFromQuote.lifeAssuredGender;
    }
    if (!merged.tobaccoConsumption && leadInputFromQuote?.smokerStatus) {
      merged.tobaccoConsumption =
        String(leadInputFromQuote.smokerStatus).toLowerCase() === "no"
          ? "No, I don't"
          : "Yes, I Do";
    }
    if (!merged.nationality) {
      merged.nationality = "Yes";
    }

    return merged;
  }, [
    journeyStages,
    quoteStageDataFromJourney,
    quoteResultFromStore,
    leadFormData,
  ]);
  const lifeAssuredName = leadData?.fullName || "";
  const lifeAssuredDob = leadData?.dateOfBirth || "";
  const isSalariedOccupation = useMemo(() => {
    const occupation = String(
      leadData?.occupation ??
        leadInput?.occupation ??
        leadResult?.occupation ??
        "",
    ).trim();
    return occupation.toLowerCase() === "salaried";
  }, [leadData?.occupation, leadInput?.occupation, leadResult?.occupation]);
  const maxCoverAmount = leadResult?.maxCoverAmount || 50000000;

  const age = useMemo(() => calculateAge(lifeAssuredDob), [lifeAssuredDob]);

  const hasAddPlusRider = useMemo(
    () => selectedRiders.has(RIDER_ID_ADD_PLUS),
    [selectedRiders],
  );

  const ecboToggleDisabledReason = useMemo(
    () =>
      getEcboToggleDisabledReason({
        paymentTermType,
        age,
        premiumRefundEnabled,
        hasAddPlusRider,
      }),
    [paymentTermType, age, premiumRefundEnabled, hasAddPlusRider],
  );

  const ropToggleDisabledReason = useMemo(
    () =>
      enhancedCoverageEnabled
        ? "Turn off Enhanced Coverage Benefit to use Return of Premium."
        : null,
    [enhancedCoverageEnabled],
  );

  const planSupportsEnhancedCoveragePayload = useMemo(
    () => selectedPlan === PLAN_NAMES.PLUS || selectedPlan === PLAN_NAMES.ELITE,
    [selectedPlan],
  );

  // For Plus with ROP OFF, cover-till-age options should follow Elite-like range logic.
  // For Plus with ROP ON, keep Plus cap behavior (hard stop at 75).
  const coverTillRangePlan = useMemo(
    () =>
      selectedPlan === PLAN_NAMES.PLUS && !premiumRefundEnabled
        ? PLAN_NAMES.ELITE
        : selectedPlan,
    [selectedPlan, premiumRefundEnabled],
  );
  const isPlusRopOff = useMemo(
    () => selectedPlan === PLAN_NAMES.PLUS && !premiumRefundEnabled,
    [selectedPlan, premiumRefundEnabled],
  );

  const _maxCoverTillAge = useMemo(
    () =>
      isPlusRopOff
        ? MAX_COVER_TILL_AGE_ABSOLUTE
        : effectiveMaxCoverTillAgeForPlan(
            leadResult?.coverTillAgeOptions,
            coverTillRangePlan,
          ),
    [isPlusRopOff, leadResult?.coverTillAgeOptions, coverTillRangePlan],
  );
  const maxPolicyTerm = useMemo(() => {
    if (isPlusRopOff) {
      if (age == null || Number.isNaN(Number(age)))
        return MAX_POLICY_TERM_YEARS;
      return Math.min(
        MAX_POLICY_TERM_YEARS,
        Math.max(0, MAX_COVER_TILL_AGE_ABSOLUTE - Number(age)),
      );
    }
    return effectiveMaxPolicyTermForPlan(
      leadResult?.policyTermOptions,
      coverTillRangePlan,
      age,
    );
  }, [isPlusRopOff, age, leadResult?.policyTermOptions, coverTillRangePlan]);

  /** Aligns lead PT with max cover-till (e.g. maturity 78 vs PT scalar 47) for grid, custom max, and premium. */
  const reconciledMaxPolicyTerm = useMemo(() => {
    if (isPlusRopOff) {
      if (age == null || Number.isNaN(Number(age)))
        return MAX_POLICY_TERM_YEARS;
      return Math.min(
        MAX_POLICY_TERM_YEARS,
        Math.max(0, MAX_COVER_TILL_AGE_ABSOLUTE - Number(age)),
      );
    }
    const r = reconciledMaxPolicyTermForCoverTillGrid({
      leadPolicyTermOptions: leadResult?.policyTermOptions,
      leadCoverTillOptions: leadResult?.coverTillAgeOptions,
      plan: coverTillRangePlan,
      attainedAgeYears: age,
    });
    if (r != null && Number.isFinite(r)) return r;
    if (maxPolicyTerm != null && Number.isFinite(Number(maxPolicyTerm))) {
      return Math.min(MAX_POLICY_TERM_YEARS, Number(maxPolicyTerm));
    }
    return MAX_POLICY_TERM_YEARS;
  }, [
    isPlusRopOff,
    leadResult?.policyTermOptions,
    leadResult?.coverTillAgeOptions,
    coverTillRangePlan,
    age,
    maxPolicyTerm,
  ]);

  const coverAmountOptions = useMemo(
    () => generateCoverAmountOptions(maxCoverAmount, selectedPlan),
    [maxCoverAmount, selectedPlan],
  );

  /** Last Life Cover radio (highest SA tier) — default selection on quote landing. */
  const defaultSelectedCoverAmount = useMemo(
    () => getDefaultSelectedCoverAmount(coverAmountOptions),
    [coverAmountOptions],
  );

  /** Same key as handleLeadComplete default cover — use when radio not set yet so byPlan cache resolves on first paint (Plus/Elite). */
  const landingDefaultCoverForPremium = useMemo(
    () => defaultSelectedCoverAmount ?? getDefaultCoverAmount(maxCoverAmount),
    [defaultSelectedCoverAmount, maxCoverAmount],
  );

  /** Product-aware min: Elite 2.0 Cr, Plus 50 Lakh. API triggers only when cover is in [effectiveMinCoverAmount, effectiveMaxCoverAmount]. */
  const effectiveMinCoverAmount = useMemo(
    () =>
      selectedPlan === PLAN_NAMES.ELITE ? MIN_COVER_ELITE : MIN_COVER_PLUS,
    [selectedPlan],
  );

  const effectiveMaxCoverAmount = useMemo(() => {
    const fromOptions = coverAmountOptions.length
      ? Math.max(...coverAmountOptions.map((o) => o.value))
      : maxCoverAmount;
    return Math.min(maxCoverAmount, fromOptions);
  }, [maxCoverAmount, coverAmountOptions]);

  const applyDefaultLifeCoverAmountSelection = useCallback(() => {
    if (defaultSelectedCoverAmount == null) return;
    setSelectedCoverAmount(defaultSelectedCoverAmount);
    setCustomCoverAmount("");
    setCoverAmountError("");
    dispatch(
      journeyActions.setQuoteCoverAmountSelection({
        selectedCoverAmount: defaultSelectedCoverAmount,
        customCoverAmount: "",
      }),
    );
  }, [
    defaultSelectedCoverAmount,
    dispatch,
    setSelectedCoverAmount,
    setCustomCoverAmount,
    setCoverAmountError,
  ]);

  /**
   * "Choose Desired" cover-till ceiling only (radios use effectiveMaxCoverTillAge / plan rules).
   * ROP on → 75. ROP off → min(85, plan max maturity, entry age + reconciled max PT).
   */
  const maxCustomCoverTillAge = useMemo(() => {
    if (selectedPlan === PLAN_NAMES.PLUS && premiumRefundEnabled) {
      return MAX_COVER_TILL_AGE_PLUS;
    }
    if (isPlusRopOff) {
      if (age == null || Number.isNaN(Number(age))) {
        return MAX_COVER_TILL_AGE_ABSOLUTE;
      }
      const ageNum = Number(age);
      const plusRopOffPtCap = Math.min(
        MAX_POLICY_TERM_YEARS,
        Math.max(0, MAX_COVER_TILL_AGE_ABSOLUTE - ageNum),
      );
      return Math.min(MAX_COVER_TILL_AGE_ABSOLUTE, ageNum + plusRopOffPtCap);
    }
    if (age == null || Number.isNaN(Number(age))) {
      return MAX_COVER_TILL_AGE_ABSOLUTE;
    }
    const desiredRangePlan =
      selectedPlan === PLAN_NAMES.PLUS && !premiumRefundEnabled
        ? PLAN_NAMES.ELITE
        : selectedPlan;
    const desiredMaturityCapRaw = effectiveMaxCoverTillAgeForPlan(
      leadResult?.coverTillAgeOptions,
      desiredRangePlan,
    );
    const desiredMaxPolicyTermRaw = effectiveMaxPolicyTermForPlan(
      leadResult?.policyTermOptions,
      desiredRangePlan,
      age,
    );
    const desiredReconciledRaw = reconciledMaxPolicyTermForCoverTillGrid({
      leadPolicyTermOptions: leadResult?.policyTermOptions,
      leadCoverTillOptions: leadResult?.coverTillAgeOptions,
      plan: desiredRangePlan,
      attainedAgeYears: age,
    });
    const desiredReconciledMaxPolicyTerm =
      desiredReconciledRaw != null &&
      Number.isFinite(Number(desiredReconciledRaw))
        ? Number(desiredReconciledRaw)
        : desiredMaxPolicyTermRaw != null &&
            Number.isFinite(Number(desiredMaxPolicyTermRaw))
          ? Math.min(MAX_POLICY_TERM_YEARS, Number(desiredMaxPolicyTermRaw))
          : MAX_POLICY_TERM_YEARS;
    const maturityCap =
      desiredMaturityCapRaw != null &&
      Number.isFinite(Number(desiredMaturityCapRaw))
        ? Number(desiredMaturityCapRaw)
        : MAX_COVER_TILL_AGE_ABSOLUTE;
    return Math.min(
      MAX_COVER_TILL_AGE_ABSOLUTE,
      maturityCap,
      Number(age) + desiredReconciledMaxPolicyTerm,
    );
  }, [
    premiumRefundEnabled,
    selectedPlan,
    isPlusRopOff,
    age,
    leadResult?.coverTillAgeOptions,
    leadResult?.policyTermOptions,
  ]);

  /** Desired text input PT ceiling: Plus+ROP No follows Elite-style PT range, others follow selected plan. */
  const desiredInputMaxPolicyTermCeiling = useMemo(() => {
    if (isPlusRopOff) {
      if (age == null || Number.isNaN(Number(age)))
        return MAX_POLICY_TERM_YEARS;
      return Math.min(
        MAX_POLICY_TERM_YEARS,
        Math.max(0, MAX_COVER_TILL_AGE_ABSOLUTE - Number(age)),
      );
    }
    if (age == null || Number.isNaN(Number(age)))
      return reconciledMaxPolicyTerm;
    const desiredRangePlan =
      selectedPlan === PLAN_NAMES.PLUS && !premiumRefundEnabled
        ? PLAN_NAMES.ELITE
        : selectedPlan;
    const desiredMaxPolicyTermRaw = effectiveMaxPolicyTermForPlan(
      leadResult?.policyTermOptions,
      desiredRangePlan,
      age,
    );
    const desiredReconciledRaw = reconciledMaxPolicyTermForCoverTillGrid({
      leadPolicyTermOptions: leadResult?.policyTermOptions,
      leadCoverTillOptions: leadResult?.coverTillAgeOptions,
      plan: desiredRangePlan,
      attainedAgeYears: age,
    });
    if (
      desiredReconciledRaw != null &&
      Number.isFinite(Number(desiredReconciledRaw))
    ) {
      return Number(desiredReconciledRaw);
    }
    if (
      desiredMaxPolicyTermRaw != null &&
      Number.isFinite(Number(desiredMaxPolicyTermRaw))
    ) {
      return Math.min(MAX_POLICY_TERM_YEARS, Number(desiredMaxPolicyTermRaw));
    }
    return reconciledMaxPolicyTerm;
  }, [
    isPlusRopOff,
    selectedPlan,
    premiumRefundEnabled,
    age,
    leadResult?.policyTermOptions,
    leadResult?.coverTillAgeOptions,
    reconciledMaxPolicyTerm,
  ]);

  // Keep radio options aligned with desired-input max ceiling.
  // If desired max maturity is 78, radios should start from 78 then step by 5.
  const coverTillPolicyPairs = useMemo(
    () =>
      buildCoverTillPolicyTermPairs({
        maxCoverTillAge: maxCustomCoverTillAge,
        attainedAgeYears: age,
        maxPolicyTermFromLead: desiredInputMaxPolicyTermCeiling,
      }),
    [maxCustomCoverTillAge, age, desiredInputMaxPolicyTermCeiling],
  );

  const coverTillToPolicyTerm = useMemo(() => {
    const m = new Map();
    coverTillPolicyPairs.forEach((p) => m.set(p.coverTillAge, p.policyTerm));
    return m;
  }, [coverTillPolicyPairs]);

  const coverTillAgeOptions = useMemo(
    () => coverTillPolicyPairs.map((p) => p.coverTillAge),
    [coverTillPolicyPairs],
  );

  const _policyTermOptions = useMemo(
    () =>
      [...new Set(coverTillPolicyPairs.map((p) => p.policyTerm))].sort(
        (a, b) => a - b,
      ),
    [coverTillPolicyPairs],
  );

  const _maxCoverTillByPolicyTermCap = useMemo(() => {
    if (age == null || Number.isNaN(Number(age))) return null;
    return Number(age) + reconciledMaxPolicyTerm;
  }, [age, reconciledMaxPolicyTerm]);

  const minCoverTillAgeForPolicyTerm = useMemo(() => {
    if (age == null || Number.isNaN(Number(age))) return null;
    return Number(age) + MIN_POLICY_TERM_YEARS;
  }, [age]);
  const formattedDob = useMemo(
    () => formatDateDDMMYYYY(lifeAssuredDob),
    [lifeAssuredDob],
  );

  const currentCoverTillAge = customCoverTillAge
    ? parseInt(customCoverTillAge, 10)
    : selectedCoverTillAge;
  const regularPayPolicyTerm = selectedPolicyTerm || 30;

  /** Policy term to send to API: derive from cover till age when available so refetch uses correct term even before selectedPolicyTerm state updates */
  const effectivePolicyTermForApi = useMemo(() => {
    const ageVal = currentCoverTillAge;
    const maxPt = customCoverTillAge
      ? desiredInputMaxPolicyTermCeiling
      : reconciledMaxPolicyTerm;
    if (
      ageVal != null &&
      !Number.isNaN(Number(ageVal)) &&
      coverTillToPolicyTerm.has(ageVal)
    ) {
      return coverTillToPolicyTerm.get(ageVal);
    }
    if (ageVal != null && !Number.isNaN(Number(ageVal)) && age != null) {
      const calculatedTerm = Number(ageVal) - Number(age);
      if (calculatedTerm >= MIN_POLICY_TERM_YEARS && calculatedTerm <= maxPt) {
        return calculatedTerm;
      }
      if (!customCoverTillAge) {
        return selectedPolicyTerm ?? null;
      }
      return null;
    }
    return selectedPolicyTerm ?? null;
  }, [
    currentCoverTillAge,
    customCoverTillAge,
    coverTillToPolicyTerm,
    selectedPolicyTerm,
    age,
    desiredInputMaxPolicyTermCeiling,
    reconciledMaxPolicyTerm,
  ]);

  /**
   * Policy term for premium/cache for a given plan (not necessarily `selectedPlan` yet).
   * Plan switch must use this: `effectivePolicyTermForApi` is derived from the *current* plan, so passing
   * `policyTermForPremium` into `onPlanChange` used Plus's cover-till→PT map when switching to Elite,
   * yielding the wrong cache key / primary cell (often a lower premium).
   */
  const resolvePolicyTermForApiForPlan = useCallback(
    (plan) => {
      const maxCt = effectiveMaxCoverTillAgeForPlan(
        leadResult?.coverTillAgeOptions,
        plan,
      );
      const maxPtFromLead = effectiveMaxPolicyTermForPlan(
        leadResult?.policyTermOptions,
        plan,
        age,
      );
      const rec = reconciledMaxPolicyTermForCoverTillGrid({
        leadPolicyTermOptions: leadResult?.policyTermOptions,
        leadCoverTillOptions: leadResult?.coverTillAgeOptions,
        plan,
        attainedAgeYears: age,
      });
      const reconciledMaxPt =
        rec != null && Number.isFinite(rec)
          ? rec
          : maxPtFromLead != null && Number.isFinite(Number(maxPtFromLead))
            ? Math.min(MAX_POLICY_TERM_YEARS, Number(maxPtFromLead))
            : MAX_POLICY_TERM_YEARS;

      const pairs =
        maxCt != null && age != null && !Number.isNaN(Number(age))
          ? buildCoverTillPolicyTermPairs({
              maxCoverTillAge: maxCt,
              attainedAgeYears: age,
              maxPolicyTermFromLead: reconciledMaxPt,
            })
          : [];
      const coverTillToPt = new Map(
        pairs.map((p) => [p.coverTillAge, p.policyTerm]),
      );

      const ageVal = currentCoverTillAge;

      if (
        ageVal != null &&
        !Number.isNaN(Number(ageVal)) &&
        coverTillToPt.has(ageVal)
      ) {
        return coverTillToPt.get(ageVal);
      }
      if (ageVal != null && !Number.isNaN(Number(ageVal)) && age != null) {
        const calculatedTerm = Number(ageVal) - Number(age);
        if (
          calculatedTerm >= MIN_POLICY_TERM_YEARS &&
          calculatedTerm <= reconciledMaxPt
        ) {
          return calculatedTerm;
        }
        if (!customCoverTillAge) {
          return selectedPolicyTerm ?? null;
        }
        return null;
      }
      return selectedPolicyTerm ?? null;
    },
    [
      leadResult?.coverTillAgeOptions,
      leadResult?.policyTermOptions,
      age,
      currentCoverTillAge,
      customCoverTillAge,
      selectedPolicyTerm,
    ],
  );

  const limitedPayOptions = useMemo(() => {
    const policyTerm =
      effectivePolicyTermForApi ??
      selectedPolicyTerm ??
      reconciledMaxPolicyTerm ??
      50;
    return getLimitedPayOptionsForPlan(selectedPlan, policyTerm);
  }, [
    selectedPlan,
    effectivePolicyTermForApi,
    selectedPolicyTerm,
    reconciledMaxPolicyTerm,
  ]);

  const selectedRidersList = useMemo(
    () =>
      Array.from(selectedRiders)
        .map((id) => {
          const rider = RIDERS_DATA.find((r) => r.id === id);
          if (!rider) return null;
          return { ...rider, selectedCover: riderCovers[id] || rider.minCover };
        })
        .filter(Boolean),
    [selectedRiders, riderCovers],
  );

  const paymentTermLabel = useMemo(() => {
    if (!paymentTermType) return "--";
    if (paymentTermType === "Limited Pay")
      return `Limited Pay · For ${selectedLimitedPayTerm} Years`;
    return `Regular Pay · For ${regularPayPolicyTerm} Years`;
  }, [paymentTermType, selectedLimitedPayTerm, regularPayPolicyTerm]);

  /** Resolved cover: from custom (parsed, in range, quantized to ₹1L steps) or from radio. Matches grid + workbook. */
  const effectiveCoverAmount = useMemo(() => {
    const parsedCustom = customCoverAmount
      ? parseCoverAmountInput(customCoverAmount)
      : null;
    const rawInRange =
      parsedCustom != null &&
      parsedCustom >= effectiveMinCoverAmount &&
      parsedCustom <= effectiveMaxCoverAmount
        ? parsedCustom
        : null;
    const inRangeCustom =
      rawInRange != null
        ? quantizeCoverAmountToRupeesStep(
            rawInRange,
            effectiveMinCoverAmount,
            effectiveMaxCoverAmount,
          )
        : null;
    // Radio selection: trust displayed options (already stepped); only require <= max so premium API runs
    const fromRadio =
      selectedCoverAmount != null &&
      selectedCoverAmount <= effectiveMaxCoverAmount
        ? selectedCoverAmount
        : null;
    const num = inRangeCustom ?? fromRadio;
    return num > 0 ? num : null;
  }, [
    customCoverAmount,
    selectedCoverAmount,
    effectiveMinCoverAmount,
    effectiveMaxCoverAmount,
  ]);

  // Custom "Choose Desired" inputs should not reuse cover-level default cache rows
  // because those may belong to a different policy term and show stale premium.
  const hasCustomDesiredInput = useMemo(
    () =>
      String(customCoverAmount || "").trim() !== "" ||
      String(customCoverTillAge || "").trim() !== "",
    [customCoverAmount, customCoverTillAge],
  );

  const {
    riderCoverDrafts,
    riderCoverErrors,
    hasRiderInputErrors,
    handleRiderCoverInputChange,
    handleRiderCoverInputBlur,
    resolveRiderCoversForSubmit,
  } = useQuoteRiders({
    selectedRiders,
    riderCovers,
    handleRiderCoverChange,
    syncRiderCovers,
    effectiveCoverAmount,
    markUserEditedQuote,
    ridersData: RIDERS_DATA,
  });

  const quotePremiumContextKey = useMemo(
    () =>
      buildPremiumContextKey({
        premiumFrequency,
        paymentTermType,
        selectedLimitedPayTerm,
        // Use current UI toggles so cache/context key matches what user sees.
        premiumRefundEnabled: !!premiumRefundEnabled,
        enhancedCoverageEnabled: !!enhancedCoverageEnabled,
        selectedRiderIds: Array.from(selectedRiders),
        riderCovers,
      }),
    [
      premiumFrequency,
      paymentTermType,
      selectedLimitedPayTerm,
      premiumRefundEnabled,
      enhancedCoverageEnabled,
      selectedRiders,
      riderCovers,
    ],
  );

  // Use multi-dimension result for current plan/cover (from global or byPlan when user switched).
  // If Redux "current" multi blob is for another product (e.g. Plus premium after UI defaulted to Elite),
  // prefer the 3-level cache for selectedPlan instead of showing the wrong amount.
  const effectiveMultiResult = useMemo(() => {
    const multi = multiDimensionPremiumResult;
    const hasMultiStructure =
      multi?.regularPayResult != null || multi?.limitedPayResults != null;

    const expectedCode = selectedPlan ? PLAN_PRODUCT_CODES[selectedPlan] : null;
    const multiProductCode =
      multi?.primaryResult?.productCode ??
      multi?.regularPayResult?.productCode ??
      null;
    const multiMismatchPlan =
      hasMultiStructure &&
      !!expectedCode &&
      multiProductCode != null &&
      String(multiProductCode).trim() !== "" &&
      String(multiProductCode).trim() !== String(expectedCode).trim();

    const fromByPlan = (() => {
      if (!multiDimensionByPlan || !selectedPlan) return null;
      const coverKey =
        effectiveCoverAmount != null
          ? String(effectiveCoverAmount)
          : String(landingDefaultCoverForPremium);
      const coverNode = multiDimensionByPlan[selectedPlan]?.[coverKey] ?? null;
      if (!coverNode || typeof coverNode !== "object") return null;
      const ptKey =
        effectivePolicyTermForApi != null
          ? String(effectivePolicyTermForApi)
          : null;
      const contextual = getContextAwareMultiResult(
        coverNode,
        ptKey,
        quotePremiumContextKey,
      );
      if (contextual) return contextual;
      // Fallback for refresh/new-browser when context-keyed cache is unavailable:
      // use plain policy-term keyed node if present.
      if (
        ptKey != null &&
        coverNode?.[ptKey] &&
        typeof coverNode[ptKey] === "object"
      ) {
        return coverNode[ptKey];
      }
      if (hasCustomDesiredInput) return null;
      if (coverNode._default) return coverNode._default;
      return null;
    })();

    let _chosenSource = "none";
    let chosen = null;
    const multiProductCodeMissing =
      multiProductCode == null || String(multiProductCode).trim() === "";
    if (multiMismatchPlan) {
      _chosenSource = fromByPlan
        ? "byPlan_due_to_mismatch"
        : "null_due_to_mismatch";
      chosen = fromByPlan ?? null;
    } else if (hasMultiStructure && fromByPlan && multiProductCodeMissing) {
      // Global multi blob without productCode is ambiguous during fast tab switches/back navigation.
      // Prefer plan-keyed cache for the active plan/dimensions to avoid cross-plan stale premium display.
      _chosenSource = "byPlan_due_to_missing_product_code";
      chosen = fromByPlan;
    } else if (hasMultiStructure) {
      _chosenSource = "global_multi";
      chosen = multi;
    } else if (fromByPlan) {
      _chosenSource = "byPlan_fallback";
      chosen = fromByPlan;
    } else {
      _chosenSource = multi ? "global_non_struct" : "null";
      chosen = multi ?? null;
    }
    return chosen;
  }, [
    multiDimensionPremiumResult,
    multiDimensionByPlan,
    selectedPlan,
    effectiveCoverAmount,
    landingDefaultCoverForPremium,
    effectivePolicyTermForApi,
    hasCustomDesiredInput,
    quotePremiumContextKey,
  ]);

  // Prefer dimension-specific result (frequency + payment term + LP years) so changing options updates display even before Redux sync
  const displayPremiumResult = useMemo(() => {
    if (!effectiveMultiResult) return premiumResult;
    const termType = paymentTermType === "Limited Pay" ? "LP" : "RP";
    const ppt = paymentTermType === "Limited Pay" ? selectedLimitedPayTerm : 0;
    const fromDimensions = getPremiumResultForDimensions(
      effectiveMultiResult,
      premiumFrequency ||
        effectiveMultiResult?.primaryPremiumPaymentMode ||
        "Yearly",
      termType,
      ppt ?? 0,
    );
    return fromDimensions || premiumResult;
  }, [
    effectiveMultiResult,
    premiumFrequency,
    paymentTermType,
    selectedLimitedPayTerm,
    premiumResult,
  ]);

  const finalDiscountedPremium =
    displayPremiumResult?.finalDiscountedPremium != null
      ? Number(displayPremiumResult.finalDiscountedPremium)
      : (displayPremiumResult?.totalPayableAmount ??
        displayPremiumResult?.premiumAmount ??
        0);
  const riderPremiumSum = useMemo(() => {
    let sum = 0;
    if (displayPremiumResult?.riderADBPremium != null)
      sum += Number(displayPremiumResult.riderADBPremium);
    if (displayPremiumResult?.riderADDPremium != null)
      sum += Number(displayPremiumResult.riderADDPremium);
    if (displayPremiumResult?.riderADDPlusPremium != null)
      sum += Number(displayPremiumResult.riderADDPlusPremium);
    if (displayPremiumResult?.riderCIPremium != null)
      sum += Number(displayPremiumResult.riderCIPremium);
    return sum;
  }, [displayPremiumResult]);
  const totalPayableAmount =
    displayPremiumResult?.totalPayableAmount != null
      ? Number(displayPremiumResult.totalPayableAmount)
      : riderPremiumSum > 0
        ? finalDiscountedPremium + riderPremiumSum
        : finalDiscountedPremium;
  const _finalPremium =
    displayPremiumResult?.finalPremium != null
      ? Number(displayPremiumResult.finalPremium)
      : totalPayableAmount;
  const premiumSavingsAmount =
    displayPremiumResult?.premiumSavingsAmount != null
      ? Number(displayPremiumResult.premiumSavingsAmount)
      : 0;
  const onlineDiscount = displayPremiumResult?.onlineDiscount ?? "10%";
  const salariedDiscount = displayPremiumResult?.salariedDiscount ?? "5%";

  const riderPremiumFromApi = useMemo(
    () => ({
      "accidental-death":
        displayPremiumResult?.riderADBPremium != null
          ? Number(displayPremiumResult.riderADBPremium)
          : null,
      "accidental-death-disability":
        displayPremiumResult?.riderADDPremium != null
          ? Number(displayPremiumResult.riderADDPremium)
          : null,
      "accidental-death-disability-plus":
        displayPremiumResult?.riderADDPlusPremium != null
          ? Number(displayPremiumResult.riderADDPlusPremium)
          : null,
      "critical-illness":
        displayPremiumResult?.riderCIPremium != null
          ? Number(displayPremiumResult.riderCIPremium)
          : null,
    }),
    [displayPremiumResult],
  );

  /** Rider premium from API only (selected frequency). No hardcoded fallback so UI shows "—" until sheet returns value. */
  const getRiderPremium = useCallback(
    (riderId) => {
      const fromApi = riderPremiumFromApi[riderId];
      return fromApi != null ? Number(fromApi) : null;
    },
    [riderPremiumFromApi],
  );

  // RP/LP current premium for selected frequency; prefer discounted/current-year premium.
  const regularPayPremium = useMemo(() => {
    const result = getPremiumResultForDimensions(
      effectiveMultiResult,
      premiumFrequency ||
        effectiveMultiResult?.primaryPremiumPaymentMode ||
        "Yearly",
      "Regular Pay",
      0,
    );
    const val =
      result?.finalDiscountedPremium ??
      result?.totalPayableAmount ??
      result?.premiumAmount;
    return val != null ? Number(val) : null;
  }, [effectiveMultiResult, premiumFrequency]);
  const getLimitedPayPremiumForTerm = useCallback(
    (years) => {
      if (!effectiveMultiResult) return null;
      const mode =
        premiumFrequency ||
        effectiveMultiResult?.primaryPremiumPaymentMode ||
        "Yearly";
      const combined = getPremiumResultForDimensions(
        effectiveMultiResult,
        mode,
        "LP",
        years,
      );
      const val =
        combined?.finalDiscountedPremium ??
        combined?.totalPayableAmount ??
        combined?.premiumAmount;
      if (val != null) return Number(val);
      const lp = effectiveMultiResult.limitedPayResults;
      if (!lp) return null;
      const fromLp =
        lp[String(years)]?.finalDiscountedPremium ??
        lp[years]?.finalDiscountedPremium ??
        lp[String(years)]?.totalPayableAmount ??
        lp[years]?.totalPayableAmount ??
        lp[String(years)]?.premiumAmount ??
        lp[years]?.premiumAmount;
      return fromLp != null ? Number(fromLp) : null;
    },
    [effectiveMultiResult, premiumFrequency],
  );
  const getLimitedPaySavingsForTerm = useCallback(
    (years) => {
      if (!effectiveMultiResult) return null;
      const mode =
        premiumFrequency ||
        effectiveMultiResult?.primaryPremiumPaymentMode ||
        "Yearly";
      const combined = getPremiumResultForDimensions(
        effectiveMultiResult,
        mode,
        "LP",
        years,
      );
      const val = combined?.premiumSavingsAmount;
      if (val != null) return Number(val);
      const lp = effectiveMultiResult.limitedPayResults;
      if (!lp) return null;
      const fromLp =
        lp[String(years)]?.premiumSavingsAmount ??
        lp[years]?.premiumSavingsAmount;
      return fromLp != null ? Number(fromLp) : null;
    },
    [effectiveMultiResult, premiumFrequency],
  );

  const isLimitedPayTermSuccessful = useCallback(
    (years) => {
      if (!effectiveMultiResult) return true;
      const mode =
        premiumFrequency ||
        effectiveMultiResult?.primaryPremiumPaymentMode ||
        "Yearly";
      const combined = getPremiumResultForDimensions(
        effectiveMultiResult,
        mode,
        "LP",
        years,
      );
      if (combined) {
        return combined.calculationSuccessful !== false;
      }
      const lp = effectiveMultiResult?.limitedPayResults;
      const fromLp = lp?.[String(years)] ?? lp?.[years] ?? null;
      if (fromLp) {
        return fromLp.calculationSuccessful !== false;
      }
      return true;
    },
    [effectiveMultiResult, premiumFrequency],
  );

  const displayLimitedPayOptions = useMemo(() => {
    const filtered = limitedPayOptions.filter((years) =>
      isLimitedPayTermSuccessful(years),
    );
    return filtered.length > 0 ? filtered : limitedPayOptions;
  }, [limitedPayOptions, isLimitedPayTermSuccessful]);

  const limitedPayOptionsScrollRef = useRef(null);
  const [limitedPayScrollArrows, setLimitedPayScrollArrows] = useState({
    canScrollLeft: false,
    canScrollRight: false,
  });

  const updateLimitedPayScrollArrows = useCallback(() => {
    const el = limitedPayOptionsScrollRef.current;
    if (!el) {
      setLimitedPayScrollArrows({
        canScrollLeft: false,
        canScrollRight: false,
      });
      return;
    }
    const maxScrollLeft = el.scrollWidth - el.clientWidth;
    setLimitedPayScrollArrows({
      canScrollLeft: el.scrollLeft > 2,
      canScrollRight: el.scrollLeft < maxScrollLeft - 2,
    });
  }, []);

  const scrollLimitedPayOptions = useCallback((direction) => {
    const el = limitedPayOptionsScrollRef.current;
    if (!el) return;
    const scrollAmount = Math.max(176, Math.floor(el.clientWidth * 0.75));
    el.scrollBy({
      left: direction === "left" ? -scrollAmount : scrollAmount,
      behavior: "smooth",
    });
  }, []);

  useLayoutEffect(() => {
    updateLimitedPayScrollArrows();
  }, [displayLimitedPayOptions, updateLimitedPayScrollArrows]);

  useEffect(() => {
    const el = limitedPayOptionsScrollRef.current;
    if (!el) return undefined;
    const observer = new ResizeObserver(() => updateLimitedPayScrollArrows());
    observer.observe(el);
    return () => observer.disconnect();
  }, [displayLimitedPayOptions.length, updateLimitedPayScrollArrows]);

  const showLimitedPaySliderArrows =
    limitedPayScrollArrows.canScrollLeft ||
    limitedPayScrollArrows.canScrollRight;

  const displayCoverAmount = effectiveCoverAmount
    ? formatLakhs(effectiveCoverAmount)
    : "";

  /** True when user is on custom desired cover and input resolves to a valid quantized SA (not digits-only parse — supports 2.4 Cr, etc.). */
  const isCustomCoverAmountSelected = useMemo(() => {
    if (!String(customCoverAmount || "").trim() || coverAmountError)
      return false;
    return effectiveCoverAmount != null && selectedCoverAmount == null;
  }, [
    customCoverAmount,
    coverAmountError,
    effectiveCoverAmount,
    selectedCoverAmount,
  ]);

  const desiredCoverInputValue = useMemo(() => {
    if (customCoverAmount !== "") return customCoverAmount;
    return "";
  }, [customCoverAmount, selectedCoverAmount]);

  const isCustomCoverTillAgeSelected = useMemo(() => {
    if (
      customCoverTillAge === "" ||
      customCoverTillAge == null ||
      coverTillAgeError
    )
      return false;
    const n = parseInt(String(customCoverTillAge), 10);
    const minAllowed = minCoverTillAgeForPolicyTerm ?? 1;
    return !isNaN(n) && n >= minAllowed && n <= maxCustomCoverTillAge;
  }, [
    customCoverTillAge,
    coverTillAgeError,
    maxCustomCoverTillAge,
    minCoverTillAgeForPolicyTerm,
  ]);

  const displayCoverTillAge = useMemo(() => {
    if (customCoverTillAge !== "" && customCoverTillAge != null) {
      const num = parseInt(String(customCoverTillAge), 10);
      const minAllowed = minCoverTillAgeForPolicyTerm ?? 1;
      if (!isNaN(num) && num >= minAllowed && num <= maxCustomCoverTillAge)
        return `${num} Years`;
    }
    if (selectedCoverTillAge) return `${selectedCoverTillAge} Years`;
    return "";
  }, [
    selectedCoverTillAge,
    customCoverTillAge,
    maxCustomCoverTillAge,
    minCoverTillAgeForPolicyTerm,
  ]);

  const isActiveGuidedStepReady = useMemo(() => {
    if (!activeGuidedStep) return false;
    switch (activeGuidedStep) {
      case GUIDED_QUOTE_STEPS.PLAN:
        return selectedPlan != null;
      case GUIDED_QUOTE_STEPS.FREQUENCY:
        return isValidPremiumFrequency(premiumFrequency);
      case GUIDED_QUOTE_STEPS.LIFE_COVER:
        return (
          (selectedCoverAmount != null && !coverAmountError) ||
          isCustomCoverAmountSelected
        );
      case GUIDED_QUOTE_STEPS.COVER_TILL_AGE:
        return (
          (selectedCoverTillAge != null && !coverTillAgeError) ||
          isCustomCoverTillAgeSelected
        );
      case GUIDED_QUOTE_STEPS.PAYMENT_TERM:
        return Boolean(paymentTermType);
      default:
        return false;
    }
  }, [
    activeGuidedStep,
    selectedPlan,
    premiumFrequency,
    selectedCoverAmount,
    coverAmountError,
    isCustomCoverAmountSelected,
    selectedCoverTillAge,
    coverTillAgeError,
    isCustomCoverTillAgeSelected,
    paymentTermType,
  ]);

  const handleGuidedNextClick = useCallback(() => {
    if (
      !isLeadGuidedFlowActive ||
      !activeGuidedStep ||
      !isActiveGuidedStepReady
    ) {
      return;
    }
    markGuidedStepComplete(activeGuidedStep);
    setGuidedStepIndex((prev) =>
      prev < GUIDED_STEP_ORDER.length ? prev + 1 : prev,
    );
  }, [
    isLeadGuidedFlowActive,
    activeGuidedStep,
    isActiveGuidedStepReady,
    markGuidedStepComplete,
  ]);

  useLayoutEffect(() => {
    if (!isLeadGuidedFlowActive || !activeGuidedStep || !guidedHintText) {
      setGuidedHintCoords(null);
      return undefined;
    }

    const updateHintPosition = () => {
      const target = document.querySelector(".quote-guided-target--active");
      if (!target) {
        setGuidedHintCoords(null);
        return;
      }
      const rect = target.getBoundingClientRect();
      const gap = 14;
      const hintEstimatedHeight = isActiveGuidedStepReady ? 148 : 88;
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;
      const placeAbove =
        spaceBelow < hintEstimatedHeight + gap && spaceAbove > spaceBelow;
      const left = rect.left + rect.width / 2;
      const top = placeAbove ? rect.top - gap : rect.bottom + gap;

      setGuidedHintPlaceAbove(placeAbove);
      setGuidedHintCoords({ top, left });
    };

    const target = document.querySelector(".quote-guided-target--active");
    target?.scrollIntoView({ behavior: "smooth", block: "center" });

    const rafId = requestAnimationFrame(updateHintPosition);
    const timeoutId = window.setTimeout(updateHintPosition, 360);

    window.addEventListener("resize", updateHintPosition);
    window.addEventListener("scroll", updateHintPosition, true);

    return () => {
      cancelAnimationFrame(rafId);
      window.clearTimeout(timeoutId);
      window.removeEventListener("resize", updateHintPosition);
      window.removeEventListener("scroll", updateHintPosition, true);
    };
  }, [
    isLeadGuidedFlowActive,
    activeGuidedStep,
    guidedHintText,
    isActiveGuidedStepReady,
  ]);

  // Default landing: ROP and ECBO must show NO (Scenarios 1–3). Single dispatch so UI always re-renders.
  useLayoutEffect(() => {
    debugLog("useLayoutEffect: applying landing defaults (mount)");
    applyLandingDefaults();
  }, [applyLandingDefaults]);

  // When lead/plan context is available, enforce landing defaults once (in case API or stored data had set them).
  useLayoutEffect(() => {
    if (
      !leadResult ||
      !assignedProductName ||
      hasAppliedLandingDefaultsRef.current
    )
      return;
    hasAppliedLandingDefaultsRef.current = true;
    debugLog("useLayoutEffect: applying landing defaults (lead/plan ready)");
    applyLandingDefaults();
  }, [leadResult, assignedProductName, applyLandingDefaults]);

  // Restore Quote UI selections from Redux so refresh/new-tab keeps user's choices.
  const hasRestoredQuoteUiFromReduxRef = useRef(false);
  const hasHydratedQuoteCacheFromStageRef = useRef(false);
  useEffect(() => {
    if (!leadResult) return;
    if (journeyRehydrated === false) return;
    if (hasHydratedQuoteCacheFromStageRef.current) return;
    if (
      !quoteStageDataFromJourney ||
      typeof quoteStageDataFromJourney !== "object"
    )
      return;

    hasHydratedQuoteCacheFromStageRef.current = true;

    const stage = quoteStageDataFromJourney;

    // Align top-level quote UI cache to backend Stage 3 snapshot.
    dispatch(
      journeyActions.setQuoteCoverAmountSelection({
        selectedCoverAmount:
          stage.customCoverAmount != null &&
          String(stage.customCoverAmount) !== ""
            ? null
            : (stage.coverAmount ?? null),
        customCoverAmount: stage.customCoverAmount ?? "",
      }),
    );
    dispatch(
      journeyActions.setQuoteCoverTillAgeSelection({
        selectedCoverTillAge:
          stage.customCoverTillAge != null &&
          String(stage.customCoverTillAge) !== ""
            ? null
            : (stage.coverTillAge ?? null),
        customCoverTillAge: stage.customCoverTillAge ?? "",
      }),
    );
    if (stage.quoteOptions && typeof stage.quoteOptions === "object") {
      dispatch(journeyActions.setQuoteOptions(stage.quoteOptions));
    }
    if (stage.quoteRiders && typeof stage.quoteRiders === "object") {
      dispatch(
        journeyActions.setQuoteRiders({
          selectedIds: Array.isArray(stage.quoteRiders.selectedIds)
            ? stage.quoteRiders.selectedIds
            : [],
          covers:
            stage.quoteRiders.covers &&
            typeof stage.quoteRiders.covers === "object"
              ? stage.quoteRiders.covers
              : {},
        }),
      );
    }
    if (stage.premiumFrequency || stage.premiumPaymentFrequency) {
      dispatch(
        journeyActions.setQuotePremiumFrequency(
          stage.premiumFrequency ?? stage.premiumPaymentFrequency,
        ),
      );
    }
    if (stage.paymentTermType) {
      dispatch(journeyActions.setQuotePaymentTermType(stage.paymentTermType));
    }
    if (stage.paymentTermType === "Limited Pay") {
      let ppt = stage.selectedLimitedPayTerm ?? stage.biPayload?.prPpt ?? null;
      if (
        (ppt == null || Number.isNaN(Number(ppt))) &&
        typeof stage.paymentTermTenure === "string"
      ) {
        const match = stage.paymentTermTenure.match(/(\d+)/);
        if (match) ppt = Number(match[1]);
      }
      if (ppt != null && !Number.isNaN(Number(ppt))) {
        dispatch(journeyActions.setQuoteSelectedLimitedPayTerm(Number(ppt)));
      }
    }
    // Resume should show saved premium values from store; clear stale API error banner.
    dispatch(journeyActions.setPremiumError(null));
  }, [leadResult, journeyRehydrated, quoteStageDataFromJourney, dispatch]);

  useEffect(() => {
    if (quoteCoverSelection !== undefined && quoteCoverSelection !== null) {
      setSelectedCoverAmount(Number(quoteCoverSelection));
    }
  }, [quoteCoverSelection]);

  useLayoutEffect(() => {
    if (!leadResult) return;
    if (journeyRehydrated === false) return;
    if (hasRestoredQuoteUiFromReduxRef.current) return;

    const quoteSource =
      quoteStageDataFromJourney && typeof quoteStageDataFromJourney === "object"
        ? quoteStageDataFromJourney
        : quoteResultFromStore;
    const quoteFrequencyFromResult =
      quoteSource?.premiumFrequency ??
      quoteSource?.premiumPaymentFrequency ??
      quoteResultFromStore?.premiumFrequency ??
      quoteResultFromStore?.premiumPaymentFrequency;
    const quotePaymentTermTypeFromResult =
      quoteSource?.paymentTermType ?? quoteResultFromStore?.paymentTermType;
    const quoteSelectedLimitedPayTermFromResult =
      quoteSource?.selectedLimitedPayTerm ??
      quoteSource?.biPayload?.prPpt ??
      quoteResultFromStore?.selectedLimitedPayTerm ??
      quoteResultFromStore?.biPayload?.prPpt;
    const quotePaymentTermTenureFromResult =
      quoteSource?.paymentTermTenure ?? quoteResultFromStore?.paymentTermTenure;

    let resolvedLimitedPayTerm = quoteSelectedLimitedPayTermFromResult;
    let didRestoreAnyQuoteUi = false;
    if (
      (resolvedLimitedPayTerm == null ||
        Number.isNaN(Number(resolvedLimitedPayTerm))) &&
      typeof quotePaymentTermTenureFromResult === "string"
    ) {
      const match = quotePaymentTermTenureFromResult.match(/(\d+)/);
      if (match) resolvedLimitedPayTerm = Number(match[1]);
    }

    // ROP / ECBO
    // For cross-browser restore, prefer backend Stage 3 snapshot first.
    const resolvedQuoteOptions =
      quoteSource?.quoteOptions ??
      quoteOptionsRedux ??
      quoteResultFromStore?.quoteOptions;
    if (resolvedQuoteOptions) {
      setPremiumRefundEnabled(!!resolvedQuoteOptions.premiumRefundEnabled);
      setEnhancedCoverageEnabled(
        !!resolvedQuoteOptions.enhancedCoverageEnabled,
      );
      didRestoreAnyQuoteUi = true;
    }

    // Premium frequency & payment term
    // For cross-browser restore, prefer backend Stage 3 snapshot first.
    const resolvedPremiumFrequency =
      quoteFrequencyFromResult ?? quotePremiumFrequencyRedux;
    const resolvedPaymentTermType =
      quotePaymentTermTypeFromResult ?? quotePaymentTermTypeRedux;
    const resolvedLimitedPayTermFromState =
      resolvedLimitedPayTerm != null &&
      !Number.isNaN(Number(resolvedLimitedPayTerm))
        ? Number(resolvedLimitedPayTerm)
        : quoteSelectedLimitedPayTermRedux != null &&
            !Number.isNaN(Number(quoteSelectedLimitedPayTermRedux))
          ? Number(quoteSelectedLimitedPayTermRedux)
          : null;

    if (resolvedPremiumFrequency && !isLeadToQuoteFirstLanding) {
      setPremiumFrequency(resolvedPremiumFrequency);
      didRestoreAnyQuoteUi = true;
    }
    if (resolvedPaymentTermType && !isLeadToQuoteFirstLanding) {
      setPaymentTermType(resolvedPaymentTermType);
      didRestoreAnyQuoteUi = true;
    }
    if (
      !isLeadToQuoteFirstLanding &&
      resolvedLimitedPayTermFromState != null &&
      !Number.isNaN(Number(resolvedLimitedPayTermFromState))
    ) {
      setSelectedLimitedPayTerm(Number(resolvedLimitedPayTermFromState));
      didRestoreAnyQuoteUi = true;
    }

    // Add-ons (riders) + their covers:
    // For cross-browser restore, prefer backend Stage 3 snapshot first.
    const quoteRidersFromResult =
      quoteSource?.quoteRiders ?? quoteResultFromStore?.quoteRiders;
    const quoteSelectedRidersFromResult =
      quoteSource?.selectedRiders ?? quoteResultFromStore?.selectedRiders;
    const quoteRiderCoversFromResult =
      quoteSource?.riderCovers ?? quoteResultFromStore?.riderCovers;

    const incomingSelectedIds = Array.isArray(
      quoteRidersFromResult?.selectedIds,
    )
      ? quoteRidersFromResult.selectedIds
      : Array.isArray(quoteRidersRedux?.selectedIds)
        ? quoteRidersRedux.selectedIds
        : Array.isArray(quoteSelectedRidersFromResult)
          ? quoteSelectedRidersFromResult
          : [];
    const incomingRiderCovers =
      quoteRidersFromResult?.covers &&
      typeof quoteRidersFromResult.covers === "object"
        ? quoteRidersFromResult.covers
        : quoteRidersRedux?.covers &&
            typeof quoteRidersRedux.covers === "object"
          ? quoteRidersRedux.covers
          : quoteRiderCoversFromResult &&
              typeof quoteRiderCoversFromResult === "object"
            ? quoteRiderCoversFromResult
            : {};
    const incomingSelectedIdsStr = [...incomingSelectedIds].sort().join(",");
    const localSelectedIdsStr = [...selectedRiders].sort().join(",");

    const coversMatch = incomingSelectedIds.every((id) => {
      const localVal = riderCovers?.[id];
      const incomingVal = incomingRiderCovers?.[id];
      if (localVal == null) return incomingVal == null;
      return Number(localVal) === Number(incomingVal);
    });

    if (incomingSelectedIdsStr !== localSelectedIdsStr || !coversMatch) {
      setRidersFromRedux(incomingSelectedIds, {
        ...initialRiderCovers,
        ...incomingRiderCovers,
      });
      didRestoreAnyQuoteUi = true;
    }

    const noPersistedQuoteUiState =
      quoteStageDataFromJourney == null &&
      quoteResultFromStore == null &&
      quotePremiumFrequencyRedux === "Yearly" &&
      quotePaymentTermTypeRedux === "Regular Pay" &&
      Number(quoteSelectedLimitedPayTermRedux) === 5 &&
      (quoteRidersRedux?.selectedIds?.length ?? 0) === 0 &&
      Object.keys(quoteRidersRedux?.covers ?? {}).length === 0 &&
      quoteOptionsRedux?.premiumRefundEnabled === false &&
      quoteOptionsRedux?.enhancedCoverageEnabled === false;

    if (!didRestoreAnyQuoteUi && !noPersistedQuoteUiState) {
      return;
    }

    hasRestoredQuoteUiFromReduxRef.current = true;
  }, [
    leadResult,
    journeyRehydrated,
    quoteOptionsRedux,
    quotePremiumFrequencyRedux,
    quotePaymentTermTypeRedux,
    quoteSelectedLimitedPayTermRedux,
    quoteStageDataFromJourney,
    quoteResultFromStore,
    quoteRidersRedux,
    setPremiumRefundEnabled,
    setEnhancedCoverageEnabled,
    setPremiumFrequency,
    setPaymentTermType,
    setSelectedLimitedPayTerm,
    setRidersFromRedux,
    initialRiderCovers,
    isLeadToQuoteFirstLanding,
  ]);

  // Prevent initial UI defaults from overwriting persisted quote values before restore completes.
  const canSyncQuoteUiToRedux =
    hasRestoredQuoteUiFromReduxRef.current ||
    (journeyRehydrated !== false &&
      quoteStageDataFromJourney == null &&
      quoteResultFromStore == null);

  // Restore "Life Cover" selection (cover amount + cover till age) from Redux
  // so opening the same URL in a new tab shows the same selected option.
  const hasRestoredLifeCoverSelectionRef = useRef(false);
  useLayoutEffect(() => {
    if (isLeadGuidedFlowActive) return;
    if (!leadResult) return;
    if (journeyRehydrated === false) return;
    if (hasRestoredLifeCoverSelectionRef.current) return;

    const hasAnyCachedLifeCoverState =
      quoteCoverAmountRedux != null ||
      quoteCoverTillAgeRedux != null ||
      (quoteCustomCoverAmountRedux != null &&
        quoteCustomCoverAmountRedux !== "") ||
      (quoteCustomCoverTillAgeRedux != null &&
        quoteCustomCoverTillAgeRedux !== "") ||
      quoteResultFromStore?.coverAmount != null ||
      quoteResultFromStore?.coverTillAge != null ||
      (quoteResultFromStore?.customCoverAmount != null &&
        String(quoteResultFromStore.customCoverAmount) !== "") ||
      (quoteResultFromStore?.customCoverTillAge != null &&
        String(quoteResultFromStore.customCoverTillAge) !== "");
    const waitingForStage3QuoteData =
      hasAnyCachedLifeCoverState &&
      journeyStages == null &&
      quoteStageDataFromJourney == null;
    if (waitingForStage3QuoteData) return;

    const quoteSource =
      quoteStageDataFromJourney && typeof quoteStageDataFromJourney === "object"
        ? quoteStageDataFromJourney
        : quoteResultFromStore;
    const quoteCoverAmountFromResult =
      quoteSource?.coverAmount ?? quoteResultFromStore?.coverAmount;
    const quoteCustomCoverAmountFromResult =
      quoteSource?.customCoverAmount ?? quoteResultFromStore?.customCoverAmount;
    const quoteCoverTillAgeFromResult =
      quoteSource?.coverTillAge ?? quoteResultFromStore?.coverTillAge;
    const quoteCustomCoverTillAgeFromResult =
      quoteSource?.customCoverTillAge ??
      quoteResultFromStore?.customCoverTillAge;

    // Prefer Redux UI cache first; it reflects the latest user selection in this browser session.
    const resolvedCustomCoverAmount =
      quoteCustomCoverAmountRedux != null &&
      String(quoteCustomCoverAmountRedux) !== ""
        ? String(quoteCustomCoverAmountRedux)
        : quoteCustomCoverAmountFromResult != null &&
            String(quoteCustomCoverAmountFromResult) !== ""
          ? String(quoteCustomCoverAmountFromResult)
          : "";
    const resolvedCoverAmount =
      quoteCoverAmountRedux != null
        ? quoteCoverAmountRedux
        : quoteCoverAmountFromResult;
    const resolvedCustomCoverTillAge =
      quoteCustomCoverTillAgeRedux != null &&
      String(quoteCustomCoverTillAgeRedux) !== ""
        ? String(quoteCustomCoverTillAgeRedux)
        : quoteCustomCoverTillAgeFromResult != null &&
            String(quoteCustomCoverTillAgeFromResult) !== ""
          ? String(quoteCustomCoverTillAgeFromResult)
          : "";
    const resolvedCoverTillAge =
      quoteCoverTillAgeRedux != null
        ? quoteCoverTillAgeRedux
        : quoteCoverTillAgeFromResult;
    const coverOptionsReady =
      coverAmountOptions.length > 0 && defaultSelectedCoverAmount != null;
    const optionValues = new Set(
      coverAmountOptions.map((option) => option.value),
    );

    // Cover amount: prefer custom value if present; else radio; else highest tier (last option).
    if (resolvedCustomCoverAmount != null && resolvedCustomCoverAmount !== "") {
      setCustomCoverAmount(resolvedCustomCoverAmount);
      setSelectedCoverAmount(null);
    } else if (resolvedCoverAmount != null) {
      const v = Number(resolvedCoverAmount);
      if (!Number.isNaN(v) && v > 0) {
        const matchedRadio =
          coverOptionsReady && optionValues.has(v)
            ? v
            : defaultSelectedCoverAmount;
        if (matchedRadio != null) {
          setSelectedCoverAmount(matchedRadio);
          setCustomCoverAmount("");
        }
      }
    } else if (defaultSelectedCoverAmount != null) {
      setSelectedCoverAmount(defaultSelectedCoverAmount);
      setCustomCoverAmount("");
    }

    const appliedPersistedCover =
      (resolvedCustomCoverAmount != null && resolvedCustomCoverAmount !== "") ||
      (resolvedCoverAmount != null &&
        !Number.isNaN(Number(resolvedCoverAmount)) &&
        Number(resolvedCoverAmount) > 0);
    if (!coverOptionsReady && !appliedPersistedCover) {
      return;
    }

    // Cover till age: prefer custom value if present; else use selected radio value.
    if (
      resolvedCustomCoverTillAge != null &&
      resolvedCustomCoverTillAge !== ""
    ) {
      setCustomCoverTillAge(resolvedCustomCoverTillAge);
      setSelectedCoverTillAge(null);
    } else if (resolvedCoverTillAge != null) {
      const v = Number(resolvedCoverTillAge);
      if (!Number.isNaN(v) && v > 0) {
        setSelectedCoverTillAge(v);
        setCustomCoverTillAge("");
      }
    }

    // Even when persisted values are unusable (invalid / non-numeric), mark restore as complete
    // so fallback defaults (max radio option) can be applied deterministically.
    hasRestoredLifeCoverSelectionRef.current = true;
  }, [
    leadResult,
    journeyRehydrated,
    quoteCoverAmountRedux,
    quoteCustomCoverAmountRedux,
    quoteCoverTillAgeRedux,
    quoteCustomCoverTillAgeRedux,
    quoteStageDataFromJourney,
    quoteResultFromStore,
    setSelectedCoverAmount,
    setCustomCoverAmount,
    setSelectedCoverTillAge,
    setCustomCoverTillAge,
    defaultSelectedCoverAmount,
    coverAmountOptions,
    isLeadGuidedFlowActive,
    isLeadToQuoteFirstLanding,
  ]);

  // Apply highest Life Cover radio once options exist (before paint); skip when user has saved/custom cover.
  // Note: we intentionally do NOT bail on `hasPersistedLifeCoverAmountSelection()` here.
  // The restore pass above marks restoration complete even when the persisted radio
  // value couldn't be matched to the current option set (e.g. cover options not yet
  // ready, plan/maxCoverAmount shifted after a refresh/resume). In that case the
  // persisted value exists but `selectedCoverAmount` is still null, so we must apply
  // the default top-tier radio to avoid the Life Cover group appearing unselected.
  // The `needsDefault` gate below ensures we only override when the current selection
  // is actually missing or invalid against the current options.
  useLayoutEffect(() => {
    if (isLeadGuidedFlowActive) return;
    if (!leadResult || journeyRehydrated === false) return;
    if (!hasRestoredLifeCoverSelectionRef.current) return;
    if (coverAmountOptions.length === 0 || defaultSelectedCoverAmount == null) {
      return;
    }
    if (String(customCoverAmount || "").trim()) return;
    if (isCustomDesiredCoverFieldFocused) return;

    const optionValues = new Set(
      coverAmountOptions.map((option) => option.value),
    );
    const needsDefault =
      selectedCoverAmount == null ||
      !optionValues.has(Number(selectedCoverAmount));
    if (needsDefault) {
      applyDefaultLifeCoverAmountSelection();
    }
  }, [
    leadResult,
    journeyRehydrated,
    coverAmountOptions,
    customCoverAmount,
    defaultSelectedCoverAmount,
    isCustomDesiredCoverFieldFocused,
    selectedCoverAmount,
    applyDefaultLifeCoverAmountSelection,
    isLeadGuidedFlowActive,
  ]);

  // Same guard for Life Cover fields: sync only after restore, unless no persisted source exists.
  const canSyncLifeCoverToRedux =
    hasRestoredLifeCoverSelectionRef.current ||
    (journeyRehydrated !== false &&
      quoteStageDataFromJourney == null &&
      quoteResultFromStore == null);

  // Keep Redux synced with the currently selected Life Cover options.
  // This ensures the selection survives opening the same URL in a new tab.
  useEffect(() => {
    if (!leadResult) return;
    if (!canSyncLifeCoverToRedux) return;
    const nextSelected = selectedCoverAmount ?? null;
    const nextCustom = customCoverAmount ?? "";
    if (
      quoteCoverAmountRedux !== nextSelected ||
      quoteCustomCoverAmountRedux !== nextCustom
    ) {
      dispatch(
        journeyActions.setQuoteCoverAmountSelection({
          selectedCoverAmount: nextSelected,
          customCoverAmount: nextCustom,
        }),
      );
    }
  }, [
    canSyncLifeCoverToRedux,
    leadResult,
    selectedCoverAmount,
    customCoverAmount,
    quoteCoverAmountRedux,
    quoteCustomCoverAmountRedux,
    dispatch,
  ]);

  useEffect(() => {
    if (!leadResult) return;
    if (!canSyncLifeCoverToRedux) return;
    const nextSelected = selectedCoverTillAge ?? null;
    const nextCustom = customCoverTillAge ?? "";
    if (
      quoteCoverTillAgeRedux !== nextSelected ||
      quoteCustomCoverTillAgeRedux !== nextCustom
    ) {
      dispatch(
        journeyActions.setQuoteCoverTillAgeSelection({
          selectedCoverTillAge: nextSelected,
          customCoverTillAge: nextCustom,
        }),
      );
    }
  }, [
    canSyncLifeCoverToRedux,
    leadResult,
    selectedCoverTillAge,
    customCoverTillAge,
    quoteCoverTillAgeRedux,
    quoteCustomCoverTillAgeRedux,
    dispatch,
  ]);

  // Keep Redux in sync with the currently selected Add-ons (riders)
  // so refresh/new-tab restores the same rider selections.
  useEffect(() => {
    if (!leadResult) return;
    if (!canSyncQuoteUiToRedux) return;
    const selectedIds = Array.from(selectedRiders);
    const reduxSelectedIds = quoteRidersRedux?.selectedIds ?? [];
    const selectedIdsStr = [...selectedIds].sort().join(",");
    const reduxSelectedIdsStr = [...reduxSelectedIds].sort().join(",");

    if (selectedIdsStr === reduxSelectedIdsStr) {
      const reduxCovers = quoteRidersRedux?.covers ?? {};
      const coversMatch = selectedIds.every((id) => {
        const localVal = riderCovers?.[id];
        const reduxVal = reduxCovers?.[id];
        if (localVal == null) return reduxVal == null;
        return Number(localVal) === Number(reduxVal);
      });
      if (coversMatch) return;
    }

    dispatch(
      journeyActions.setQuoteRiders({
        selectedIds,
        covers: riderCovers ?? {},
      }),
    );
  }, [
    canSyncQuoteUiToRedux,
    leadResult,
    selectedRiders,
    riderCovers,
    quoteRidersRedux,
    dispatch,
  ]);

  // Sync ROP/ECBO toggles to Redux so calculateMulti and refetches use current state (Scenarios 1–3).
  useEffect(() => {
    // Avoid overwriting persisted Redux values with UI defaults before we restore from Redux.
    if (!leadResult) return;
    if (!canSyncQuoteUiToRedux) return;
    if (
      quoteOptionsRedux?.premiumRefundEnabled === premiumRefundEnabled &&
      quoteOptionsRedux?.enhancedCoverageEnabled === enhancedCoverageEnabled
    ) {
      return;
    }
    dispatch(
      journeyActions.setQuoteOptions({
        premiumRefundEnabled,
        enhancedCoverageEnabled,
      }),
    );
  }, [
    canSyncQuoteUiToRedux,
    dispatch,
    leadResult,
    premiumRefundEnabled,
    enhancedCoverageEnabled,
    quoteOptionsRedux,
  ]);

  const frequencyToUse =
    (isValidPremiumFrequency(premiumFrequency) ? premiumFrequency : null) ||
    (!isLeadGuidedFlowActive && quotePremiumFrequencyRedux) ||
    effectiveMultiResult?.primaryPremiumPaymentMode ||
    "Yearly";

  const getFrequencyMultiplier = (frequency) => {
    switch (frequency?.toLowerCase()) {
      case "yearly":
        return 1;
      case "half-yearly":
        return 2;
      case "quarterly":
        return 4;
      case "monthly":
        return 12;
      default:
        return 1;
    }
  };

  const calculateSavingsPercentage = ({
    savingAmount,
    regularPayPremium,
    policyTerm,
    frequency,
  }) => {
    const multiplier = getFrequencyMultiplier(frequency);

    const totalRegularPay = regularPayPremium * multiplier * policyTerm;

    if (!totalRegularPay || savingAmount <= 0) return 0;

    return (savingAmount * 100) / totalRegularPay;
  };

  // Persist Premium Frequency + Premium Payment Term selections in Redux
  // so refresh/new-tab restores the same UI.
  useEffect(() => {
    if (!leadResult) return;
    if (!canSyncQuoteUiToRedux) return;
    if (!isValidPremiumFrequency(premiumFrequency)) return;
    if (quotePremiumFrequencyRedux === premiumFrequency) return;
    dispatch(journeyActions.setQuotePremiumFrequency(premiumFrequency));
  }, [
    canSyncQuoteUiToRedux,
    dispatch,
    leadResult,
    premiumFrequency,
    quotePremiumFrequencyRedux,
  ]);

  useEffect(() => {
    if (!leadResult) return;
    if (!canSyncQuoteUiToRedux) return;
    if (!paymentTermType) return;
    if (quotePaymentTermTypeRedux === paymentTermType) return;
    dispatch(journeyActions.setQuotePaymentTermType(paymentTermType));
  }, [
    canSyncQuoteUiToRedux,
    dispatch,
    leadResult,
    paymentTermType,
    quotePaymentTermTypeRedux,
  ]);

  useEffect(() => {
    if (!leadResult) return;
    if (!canSyncQuoteUiToRedux) return;
    if (paymentTermType !== "Limited Pay") return;
    if (selectedLimitedPayTerm == null) return;
    if (
      Number(quoteSelectedLimitedPayTermRedux) ===
      Number(selectedLimitedPayTerm)
    ) {
      return;
    }
    dispatch(
      journeyActions.setQuoteSelectedLimitedPayTerm(selectedLimitedPayTerm),
    );
  }, [
    canSyncQuoteUiToRedux,
    dispatch,
    leadResult,
    selectedLimitedPayTerm,
    quoteSelectedLimitedPayTermRedux,
  ]);

  useEffect(() => {
    debugLog(
      "Quote mounted (effect []); current ROP/ECBO in state will log on next render",
    );
  }, []);

  // Default plan from eligibility + pincode master (only show/select plans allowed at pincode).
  // Prefer last saved quote (e.g. after summary → back to quote) so premium slice matches selection.
  useEffect(() => {
    if (isLeadGuidedFlowActive) return;
    if (!assignedProductName) return;
    if (visibleTermPlans.length === 0) return;

    const quoteSource =
      quoteStageDataFromJourney && typeof quoteStageDataFromJourney === "object"
        ? quoteStageDataFromJourney
        : quoteResultFromStore;
    const savedPlanName =
      quoteSource?.selectedPlan ?? quoteSource?.productName ?? null;
    const fromSavedQuote = resolveVisiblePlanFromSavedQuote(
      savedPlanName,
      visibleTermPlans,
    );
    if (fromSavedQuote) {
      setSelectedPlan(fromSavedQuote);
      return;
    }

    if (visibleTermPlans.length === 1) {
      setSelectedPlan(visibleTermPlans[0]);
      return;
    }

    const savedPlan =
      quoteStageDataFromJourney?.selectedPlan ||
      quoteResultFromStore?.selectedPlan;

    if (savedPlan) {
      setSelectedPlan(savedPlan);
      return;
    }
  }, [
    assignedProductName,
    visibleTermPlans,
    canOfferEliteAtPincode,
    pincodeAllowsPlus,
    setSelectedPlan,
    quoteStageDataFromJourney,
    quoteResultFromStore,
    isLeadGuidedFlowActive,
  ]);

  // Max eligible SA below Elite minimum (e.g. after edit) — fall back to Plus when Elite is no longer offered.
  useEffect(() => {
    if (selectedPlan !== PLAN_NAMES.ELITE) return;
    if (canOfferEliteAtPincode) return;
    if (!pincodeAllowsPlus) return;
    planChangeInFlightRef.current = true;
    setSelectedPlan(PLAN_NAMES.PLUS);
  }, [
    selectedPlan,
    canOfferEliteAtPincode,
    pincodeAllowsPlus,
    setSelectedPlan,
  ]);

  // Scenario 1 (SS Elite): ROP YES → change to SS Plus; track so we can change back when ROP NO.
  // ROP and ECBO are mutually exclusive (different plans), so disable ECBO when activating ROP.
  useEffect(() => {
    if (
      premiumRefundEnabled &&
      selectedPlan === "Super Suraksha Elite" &&
      pincodeAllowsPlus
    ) {
      planChangeInFlightRef.current = true;
      setEnhancedCoverageEnabled(false);
      setSelectedPlan("Super Suraksha Plus");
      setSwitchedToPlusDueToROP(true);
    }
  }, [
    premiumRefundEnabled,
    selectedPlan,
    pincodeAllowsPlus,
    setSelectedPlan,
    setSwitchedToPlusDueToROP,
    setEnhancedCoverageEnabled,
  ]);

  // Scenario 1 (SS Elite): ROP NO and we had auto-switched to Plus → change back to SS Elite
  useEffect(() => {
    if (
      !premiumRefundEnabled &&
      selectedPlan === "Super Suraksha Plus" &&
      switchedToPlusDueToROP &&
      canOfferEliteAtPincode
    ) {
      planChangeInFlightRef.current = true;
      setSelectedPlan("Super Suraksha Elite");
      setSwitchedToPlusDueToROP(false);
    }
  }, [
    premiumRefundEnabled,
    selectedPlan,
    switchedToPlusDueToROP,
    canOfferEliteAtPincode,
    setSelectedPlan,
    setSwitchedToPlusDueToROP,
  ]);

  // Plus → Elite: user at Plus cover-till cap (75) should land on Elite's highest valid option when higher ages exist (e.g. ROP off).
  useEffect(() => {
    const prev = prevSelectedPlanForCoverTillRef.current;
    prevSelectedPlanForCoverTillRef.current = selectedPlan;
    if (prev == null) return;
    if (
      prev === PLAN_NAMES.PLUS &&
      selectedPlan === PLAN_NAMES.ELITE &&
      !customCoverTillAge &&
      coverTillAgeOptions.length > 0 &&
      selectedCoverTillAge != null
    ) {
      const maxOpt = Math.max(...coverTillAgeOptions);
      if (
        selectedCoverTillAge === MAX_COVER_TILL_AGE_PLUS &&
        maxOpt > MAX_COVER_TILL_AGE_PLUS
      ) {
        setSelectedCoverTillAge(maxOpt);
        dispatch(
          journeyActions.setQuoteCoverTillAgeSelection({
            selectedCoverTillAge: maxOpt,
            customCoverTillAge: "",
          }),
        );
      }
    }
  }, [
    selectedPlan,
    customCoverTillAge,
    coverTillAgeOptions,
    selectedCoverTillAge,
    dispatch,
    setSelectedCoverTillAge,
  ]);

  // ECBO on Super Suraksha Plus stays on Plus (Life / Life Plus per ROP); clear ECBO when rules block it.
  useEffect(() => {
    if (!enhancedCoverageEnabled) return;
    if (
      paymentTermType !== "Regular Pay" ||
      age == null ||
      Number.isNaN(Number(age)) ||
      Number(age) >= ECBO_MAX_AGE_EXCLUSIVE ||
      premiumRefundEnabled ||
      selectedRiders.has(RIDER_ID_ADD_PLUS)
    ) {
      setEnhancedCoverageEnabled(false);
    }
  }, [
    enhancedCoverageEnabled,
    paymentTermType,
    age,
    premiumRefundEnabled,
    selectedRiders,
    setEnhancedCoverageEnabled,
  ]);

  // Post-paint companion of the useLayoutEffect above: same intent — fall back to the
  // default top-tier radio when, after restoration, `selectedCoverAmount` is still null
  // or no longer matches the current option set. The persisted-value early-return was
  // removed for the same reason as the layout-effect (see comment above): on
  // refresh/resume the persisted value may not have been applied yet, and the UI must
  // still settle on a valid radio so the Life Cover group isn't left unselected.
  useEffect(() => {
    if (!hasRestoredLifeCoverSelectionRef.current) return;
    if (isCustomDesiredCoverFieldFocused) return;
    if (String(customCoverAmount || "").trim()) return;
    if (quoteCoverSelection != null) return;

    const optionValues = new Set(
      coverAmountOptions.map((option) => option.value),
    );
    const needsDefault =
      defaultSelectedCoverAmount != null &&
      (selectedCoverAmount == null ||
        !optionValues.has(Number(selectedCoverAmount)));
    if (needsDefault) {
      applyDefaultLifeCoverAmountSelection();
    }
  }, [
    applyDefaultLifeCoverAmountSelection,
    coverAmountOptions,
    customCoverAmount,
    defaultSelectedCoverAmount,
    isCustomDesiredCoverFieldFocused,
    quoteCoverSelection,
    selectedCoverAmount,
  ]);

  // When cover amount, plan, frequency, payment term or policy term changes, update premium from cache or fetch for this dimension
  const policyTermForPremium =
    effectivePolicyTermForApi ??
    selectedPolicyTerm ??
    reconciledMaxPolicyTerm ??
    25;

  const getPolicyTermForCoverTillAge = useCallback(
    (ageOpt) => {
      if (coverTillToPolicyTerm.has(ageOpt)) {
        return coverTillToPolicyTerm.get(ageOpt);
      }
      if (age != null && Number(ageOpt) > Number(age)) {
        const pt = Number(ageOpt) - Number(age);
        const maxPtCeiling = customCoverTillAge
          ? desiredInputMaxPolicyTermCeiling
          : reconciledMaxPolicyTerm;
        if (pt >= MIN_POLICY_TERM_YEARS && pt <= maxPtCeiling) return pt;
      }
      return null;
    },
    [
      coverTillToPolicyTerm,
      age,
      customCoverTillAge,
      desiredInputMaxPolicyTermCeiling,
      reconciledMaxPolicyTerm,
    ],
  );

  const premiumAmountFromDimensionResult = useCallback(
    (multi) => {
      if (!multi) return null;
      const termType = paymentTermType === "Limited Pay" ? "LP" : "RP";
      const ppt =
        paymentTermType === "Limited Pay" ? selectedLimitedPayTerm : 0;
      const r = getPremiumResultForDimensions(
        multi,
        premiumFrequency || multi?.primaryPremiumPaymentMode || "Yearly",
        termType,
        ppt ?? 0,
      );
      const v =
        r?.finalDiscountedPremium ?? r?.totalPayableAmount ?? r?.premiumAmount;
      if (v != null) return Number(v);
      // Fallback: for some non-selected cover-till-age options exact dimensions
      // (especially LP term) may not exist, but bulk response still has primary result.
      const primaryVal =
        multi?.primaryResult?.finalDiscountedPremium ??
        multi?.primaryResult?.totalPayableAmount ??
        multi?.primaryResult?.premiumAmount ??
        multi?.regularPayResult?.finalDiscountedPremium ??
        multi?.regularPayResult?.totalPayableAmount ??
        multi?.regularPayResult?.premiumAmount;
      return primaryVal != null ? Number(primaryVal) : null;
    },
    [premiumFrequency, paymentTermType, selectedLimitedPayTerm],
  );

  const _getPremiumForCoverAmountOption = useCallback(
    (amount) => {
      const multi =
        Number(amount) === Number(effectiveCoverAmount)
          ? effectiveMultiResult
          : (() => {
              const coverNode =
                multiDimensionByPlan?.[selectedPlan]?.[String(amount)];
              if (!coverNode) return null;
              return getContextAwareMultiResult(
                coverNode,
                policyTermForPremium,
                quotePremiumContextKey,
              );
            })();
      const premiumFromMulti = premiumAmountFromDimensionResult(multi);
      if (premiumFromMulti != null) return premiumFromMulti;
      return 0;
    },
    [
      effectiveCoverAmount,
      effectiveMultiResult,
      multiDimensionByPlan,
      selectedPlan,
      policyTermForPremium,
      quotePremiumContextKey,
      premiumAmountFromDimensionResult,
    ],
  );

  const getPremiumForCoverTillAgeOption = useCallback(
    (ageOpt) => {
      const pt = getPolicyTermForCoverTillAge(ageOpt);
      if (pt == null) {
        return null;
      }
      let multi;
      if (Number(pt) === Number(policyTermForPremium)) {
        multi = effectiveMultiResult;
      } else {
        const coverNode =
          multiDimensionByPlan?.[selectedPlan]?.[String(effectiveCoverAmount)];
        multi = getContextAwareMultiResultStrict(
          coverNode,
          pt,
          quotePremiumContextKey,
        );
      }
      const premiumFromMulti = premiumAmountFromDimensionResult(multi);
      if (premiumFromMulti != null && Number.isFinite(premiumFromMulti)) {
        return premiumFromMulti;
      }
      return null;
    },
    [
      getPolicyTermForCoverTillAge,
      policyTermForPremium,
      effectiveMultiResult,
      multiDimensionByPlan,
      selectedPlan,
      effectiveCoverAmount,
      quotePremiumContextKey,
      premiumAmountFromDimensionResult,
    ],
  );

  // Ensure all Cover Till Age cards have premium data after refresh/new tab.
  // If any option lacks a cached multi-result, force one bulk refresh.
  const missingCoverTillPremiumData = useMemo(() => {
    if (!coverTillAgeOptions?.length) return false;
    if (!selectedPlan || effectiveCoverAmount == null) return false;
    const coverNode =
      multiDimensionByPlan?.[selectedPlan]?.[String(effectiveCoverAmount)];
    if (!coverNode) return true;
    for (const ageOpt of coverTillAgeOptions) {
      const pt = getPolicyTermForCoverTillAge(ageOpt);
      if (pt == null) continue;
      if (Number(pt) === Number(policyTermForPremium) && effectiveMultiResult)
        continue;
      const contextual = getContextAwareMultiResultStrict(
        coverNode,
        pt,
        quotePremiumContextKey,
      );
      if (!contextual) {
        return true;
      }
    }
    return false;
  }, [
    coverTillAgeOptions,
    selectedPlan,
    effectiveCoverAmount,
    multiDimensionByPlan,
    getPolicyTermForCoverTillAge,
    policyTermForPremium,
    effectiveMultiResult,
    quotePremiumContextKey,
  ]);

  const coverTillBulkRetryRef = useRef(0);
  useEffect(() => {
    coverTillBulkRetryRef.current = 0;
  }, [quotePremiumContextKey, selectedPlan, coverTillAgeOptions.length]);

  useEffect(() => {
    if (isLeadGuidedFlowActive) return;
    if (!missingCoverTillPremiumData) return;
    if (!onQuoteDimensionsChange || premiumLoading) return;
    if (isCustomDesiredCoverFieldFocused && selectedCoverAmount == null) {
      return;
    }
    if (
      !selectedPlan ||
      effectiveCoverAmount == null ||
      effectivePolicyTermForApi == null
    )
      return;
    if (coverTillBulkRetryRef.current >= 2) return;
    coverTillBulkRetryRef.current += 1;
    onQuoteDimensionsChange(
      selectedPlan,
      effectiveCoverAmount,
      effectivePolicyTermForApi,
      premiumFrequency,
      paymentTermType,
      selectedLimitedPayTerm,
      true,
    );
  }, [
    missingCoverTillPremiumData,
    onQuoteDimensionsChange,
    premiumLoading,
    selectedPlan,
    effectiveCoverAmount,
    effectivePolicyTermForApi,
    premiumFrequency,
    paymentTermType,
    selectedLimitedPayTerm,
    isCustomDesiredCoverFieldFocused,
    selectedCoverAmount,
    isLeadGuidedFlowActive,
  ]);

  const customCoverTillAgePremium = useMemo(() => {
    if (!isCustomCoverTillAgeSelected || currentCoverTillAge == null)
      return null;
    return getPremiumForCoverTillAgeOption(currentCoverTillAge);
  }, [
    isCustomCoverTillAgeSelected,
    currentCoverTillAge,
    getPremiumForCoverTillAgeOption,
  ]);

  const shouldAutoCalculateOnInitialQuoteLoad =
    quoteStageDataFromJourney == null &&
    quoteResultFromStore == null &&
    !isLeadGuidedFlowActive;

  const prevCoverRef = useRef(null);
  useEffect(() => {
    if (isLeadGuidedFlowActive) return;
    if (
      !hasUserEditedQuoteRef.current &&
      !shouldAutoCalculateOnInitialQuoteLoad
    )
      return;
    if (!effectiveCoverAmount || !selectedPlan || !onCoverAmountChange) return;
    if (planChangeInFlightRef.current) return;

    if (premiumLoading) {
      prevCoverRef.current = {
        cover: effectiveCoverAmount,
        custom: customCoverAmount,
      };
      return;
    }

    const initialGridCached = getContextAwareMultiResult(
      multiDimensionByPlan?.[selectedPlan]?.[String(effectiveCoverAmount)],
      policyTermForPremium,
      quotePremiumContextKey,
    );
    if (
      shouldAutoCalculateOnInitialQuoteLoad &&
      initialGridCached &&
      !hasUserEditedQuoteRef.current
    ) {
      prevCoverRef.current = {
        cover: effectiveCoverAmount,
        custom: customCoverAmount,
      };
      return;
    }

    if (isCustomDesiredCoverFieldFocused && selectedCoverAmount == null) {
      return;
    }

    if (
      prevCoverRef.current != null &&
      prevCoverRef.current.cover === effectiveCoverAmount &&
      prevCoverRef.current.custom === customCoverAmount
    ) {
      return;
    }
    prevCoverRef.current = {
      cover: effectiveCoverAmount,
      custom: customCoverAmount,
    };

    const delay =
      String(customCoverAmount || "").trim() !== ""
        ? CUSTOM_COVER_PREMIUM_DEBOUNCE_MS
        : 0;

    const run = () => {
      onCoverAmountChange(
        selectedPlan,
        effectiveCoverAmount,
        premiumFrequency,
        paymentTermType,
        selectedLimitedPayTerm,
        policyTermForPremium,
      );
    };

    if (delay === 0) {
      run();
      return undefined;
    }
    const t = setTimeout(run, delay);
    return () => clearTimeout(t);
  }, [
    effectiveCoverAmount,
    selectedPlan,
    onCoverAmountChange,
    shouldAutoCalculateOnInitialQuoteLoad,
    customCoverAmount,
    premiumLoading,
    premiumFrequency,
    paymentTermType,
    selectedLimitedPayTerm,
    policyTermForPremium,
    multiDimensionByPlan,
    quotePremiumContextKey,
    isCustomDesiredCoverFieldFocused,
    selectedCoverAmount,
    isLeadGuidedFlowActive,
  ]);

  useEffect(() => {
    if (isLeadGuidedFlowActive) return;
    if (
      displayLimitedPayOptions.length > 0 &&
      !displayLimitedPayOptions.includes(selectedLimitedPayTerm)
    ) {
      setSelectedLimitedPayTerm(displayLimitedPayOptions[0]);
    }
  }, [
    displayLimitedPayOptions,
    selectedLimitedPayTerm,
    isLeadGuidedFlowActive,
  ]);

  // Sync premiumResult when user changes payment term type (RP/LP) or selected LP term; pass frequency so correct premium is shown
  useEffect(() => {
    if (isLeadGuidedFlowActive) return;
    if (!onPaymentTermChange || !effectiveMultiResult) return;
    onPaymentTermChange(
      paymentTermType === "Limited Pay" ? "Limited Pay" : "Regular Pay",
      paymentTermType === "Limited Pay" ? selectedLimitedPayTerm : null,
      premiumFrequency,
    );
  }, [
    paymentTermType,
    selectedLimitedPayTerm,
    premiumFrequency,
    effectiveMultiResult,
    onPaymentTermChange,
    isLeadGuidedFlowActive,
  ]);

  // When user changes policy term or cover till age, refetch so RP/LP premiums match (LP options depend on policy term).
  // Use effectivePolicyTermForApi so cover-till-age change triggers one correct refetch (not dependent on async selectedPolicyTerm update).
  const prevDimensionsRef = useRef(null);
  const dimensionsDebounceRef = useRef(null);
  const postRestoreBulkSignatureRef = useRef(null);
  useEffect(() => {
    if (isLeadGuidedFlowActive) return;
    // On refresh/new-tab restore, run one forced bulk fetch for the restored
    // plan/cover/policy-term signature so cover-till-age recommendation text
    // is populated for the restored selection as well.
    if (
      !onQuoteDimensionsChange ||
      !selectedPlan ||
      effectiveCoverAmount == null
    )
      return;
    if (effectivePolicyTermForApi == null) return;
    if (!canSyncQuoteUiToRedux || !canSyncLifeCoverToRedux) return;
    if (hasUserEditedQuoteRef.current) return;
    const signature = `${selectedPlan}|${effectiveCoverAmount}|${effectivePolicyTermForApi}|${premiumFrequency}|${paymentTermType}|${selectedLimitedPayTerm}`;
    if (postRestoreBulkSignatureRef.current === signature) return;
    postRestoreBulkSignatureRef.current = signature;
    onQuoteDimensionsChange(
      selectedPlan,
      effectiveCoverAmount,
      effectivePolicyTermForApi,
      premiumFrequency,
      paymentTermType,
      selectedLimitedPayTerm,
      true,
    );
  }, [
    onQuoteDimensionsChange,
    selectedPlan,
    effectiveCoverAmount,
    effectivePolicyTermForApi,
    premiumFrequency,
    paymentTermType,
    selectedLimitedPayTerm,
    canSyncQuoteUiToRedux,
    canSyncLifeCoverToRedux,
    isLeadGuidedFlowActive,
  ]);

  useEffect(() => {
    if (isLeadGuidedFlowActive) return;
    if (
      !hasUserEditedQuoteRef.current &&
      !shouldAutoCalculateOnInitialQuoteLoad
    )
      return;
    if (
      !onQuoteDimensionsChange ||
      effectiveCoverAmount == null ||
      !selectedPlan ||
      effectivePolicyTermForApi == null
    )
      return;
    if (planChangeInFlightRef.current) return;

    const nextDimensions = {
      plan: selectedPlan,
      pt: effectivePolicyTermForApi,
      age: selectedCoverTillAge,
      customAge: customCoverTillAge,
    };
    if (prevDimensionsRef.current == null) {
      prevDimensionsRef.current = nextDimensions;
      // For explicit user actions (e.g. plan switch after summary back),
      // allow first corrective dimensions fetch instead of swallowing it.
      if (!hasUserEditedQuoteRef.current) return;
    } else if (
      prevDimensionsRef.current.plan === nextDimensions.plan &&
      prevDimensionsRef.current.pt === nextDimensions.pt &&
      prevDimensionsRef.current.age === nextDimensions.age &&
      prevDimensionsRef.current.customAge === nextDimensions.customAge
    ) {
      return;
    } else {
      prevDimensionsRef.current = nextDimensions;
    }

    if (dimensionsDebounceRef.current) {
      clearTimeout(dimensionsDebounceRef.current);
      dimensionsDebounceRef.current = null;
    }
    const delay =
      String(customCoverAmount || "").trim() !== ""
        ? CUSTOM_COVER_PREMIUM_DEBOUNCE_MS
        : 0;
    const run = () => {
      dimensionsDebounceRef.current = null;
      const shouldForceRefresh = String(customCoverTillAge || "").trim() !== "";
      onQuoteDimensionsChange(
        selectedPlan,
        effectiveCoverAmount,
        effectivePolicyTermForApi,
        premiumFrequency,
        paymentTermType,
        selectedLimitedPayTerm,
        shouldForceRefresh,
      );
    };
    if (delay === 0) {
      run();
      return undefined;
    }
    dimensionsDebounceRef.current = setTimeout(run, delay);
    return () => {
      if (dimensionsDebounceRef.current) {
        clearTimeout(dimensionsDebounceRef.current);
        dimensionsDebounceRef.current = null;
      }
    };
  }, [
    effectivePolicyTermForApi,
    selectedCoverTillAge,
    customCoverTillAge,
    onQuoteDimensionsChange,
    effectiveCoverAmount,
    selectedPlan,
    paymentTermType,
    selectedLimitedPayTerm,
    shouldAutoCalculateOnInitialQuoteLoad,
    customCoverAmount,
    isLeadGuidedFlowActive,
  ]);

  useEffect(() => {
    if (!isLeadGuidedFlowActive) return;
    if (
      !onQuoteDimensionsChange ||
      !selectedPlan ||
      effectiveCoverAmount == null
    ) {
      return;
    }

    const policyTermForBulk =
      effectivePolicyTermForApi ??
      selectedPolicyTerm ??
      reconciledMaxPolicyTerm ??
      25;
    const frequencyForBulk = isValidPremiumFrequency(premiumFrequency)
      ? premiumFrequency
      : "Yearly";

    const triggerBulkForStep = (stepKey, canTrigger = true) => {
      if (!guidedSelections[stepKey]) return;
      if (guidedBulkTriggeredForStepsRef.current.has(stepKey)) return;
      if (!canTrigger) return;

      guidedBulkTriggeredForStepsRef.current.add(stepKey);
      setGuidedBulkLoading(true);

      void Promise.resolve(
        onQuoteDimensionsChange(
          selectedPlan,
          effectiveCoverAmount,
          policyTermForBulk,
          frequencyForBulk,
          paymentTermType,
          selectedLimitedPayTerm,
          true,
        ),
      ).finally(() => {
        setGuidedBulkLoading(false);
        if (stepKey === GUIDED_QUOTE_STEPS.PAYMENT_TERM) {
          setIsGuidedFlowActive(false);
        }
      });
    };

    triggerBulkForStep(GUIDED_QUOTE_STEPS.LIFE_COVER);

    triggerBulkForStep(
      GUIDED_QUOTE_STEPS.COVER_TILL_AGE,
      (selectedCoverTillAge != null ||
        String(customCoverTillAge || "").trim() !== "") &&
        effectivePolicyTermForApi != null,
    );

    triggerBulkForStep(
      GUIDED_QUOTE_STEPS.PAYMENT_TERM,
      Boolean(paymentTermType),
    );
  }, [
    isLeadGuidedFlowActive,
    guidedSelections,
    onQuoteDimensionsChange,
    selectedPlan,
    effectiveCoverAmount,
    effectivePolicyTermForApi,
    selectedPolicyTerm,
    reconciledMaxPolicyTerm,
    premiumFrequency,
    paymentTermType,
    selectedLimitedPayTerm,
    selectedCoverTillAge,
    customCoverTillAge,
  ]);

  // When user adds/removes a rider or changes rider cover, refetch premium so updated premium with riders is shown.
  // Only proceed when rider selections or covers actually changed — not when paymentTermType/LP changes.
  // Uses resolveRiderCoversForSubmit() for key + payload so keys match submit/frequency behavior (all rider types).
  const prevRiderRef = useRef(null);
  const draftRiderPremiumTimerRef = useRef(null);
  useEffect(() => {
    if (
      !hasUserEditedQuoteRef.current &&
      !shouldAutoCalculateOnInitialQuoteLoad
    )
      return;
    if (
      !onRiderChange ||
      effectiveCoverAmount == null ||
      !selectedPlan ||
      selectedPolicyTerm == null
    )
      return;
    if (planChangeInFlightRef.current) return;

    const resolved = resolveRiderCoversForSubmit();
    if (resolved.error) {
      return;
    }
    const coversPayload = resolved.covers ?? riderCovers;
    const riderKey = JSON.stringify({
      ids: Array.from(selectedRiders || []).sort(),
      covers: coversPayload,
    });
    if (prevRiderRef.current == null) {
      prevRiderRef.current = riderKey;
      return;
    }
    if (prevRiderRef.current === riderKey) {
      return;
    }
    prevRiderRef.current = riderKey;

    onRiderChange(
      selectedPlan,
      effectiveCoverAmount,
      selectedPolicyTerm,
      premiumFrequency,
      paymentTermType,
      selectedLimitedPayTerm,
      Array.from(selectedRiders),
      coversPayload,
    );
  }, [
    selectedRiders,
    riderCovers,
    onRiderChange,
    selectedPlan,
    effectiveCoverAmount,
    selectedPolicyTerm,
    paymentTermType,
    selectedLimitedPayTerm,
    shouldAutoCalculateOnInitialQuoteLoad,
    premiumFrequency,
  ]);

  // Draft edits update riderCoverDrafts before committed riderCovers (debounce). Refetch when effective SA
  // (from resolve) differs from committed state — e.g. after changing frequency with draft SA, then editing cover.
  useEffect(() => {
    if (
      !hasUserEditedQuoteRef.current &&
      !shouldAutoCalculateOnInitialQuoteLoad
    )
      return;
    if (
      !onRiderChange ||
      effectiveCoverAmount == null ||
      !selectedPlan ||
      selectedPolicyTerm == null
    )
      return;
    if (planChangeInFlightRef.current) return;
    if (!selectedRiders?.size) return;

    clearTimeout(draftRiderPremiumTimerRef.current);
    draftRiderPremiumTimerRef.current = setTimeout(() => {
      const resolved = resolveRiderCoversForSubmit();
      if (resolved.error || !resolved.covers) return;
      const ids = Array.from(selectedRiders);
      if (!effectiveRiderCoversDiffer(resolved.covers, riderCovers, ids)) {
        return;
      }
      const riderKey = JSON.stringify({
        ids: [...ids].sort(),
        covers: resolved.covers,
      });
      prevRiderRef.current = riderKey;
      onRiderChange(
        selectedPlan,
        effectiveCoverAmount,
        selectedPolicyTerm,
        premiumFrequency,
        paymentTermType,
        selectedLimitedPayTerm,
        ids,
        resolved.covers,
      );
    }, RIDER_COVER_PREMIUM_DEBOUNCE_MS);

    return () => clearTimeout(draftRiderPremiumTimerRef.current);
  }, [
    riderCoverDrafts,
    selectedRiders,
    riderCovers,
    onRiderChange,
    selectedPlan,
    effectiveCoverAmount,
    selectedPolicyTerm,
    paymentTermType,
    selectedLimitedPayTerm,
    shouldAutoCalculateOnInitialQuoteLoad,
    premiumFrequency,
  ]);

  // When ROP/ECBO toggles change, refetch premium so quote reflects updated plan options.
  // Use the same bulk path as rider changes.
  const prevQuoteOptionsRef = useRef(null);
  useEffect(() => {
    if (
      !hasUserEditedQuoteRef.current &&
      !shouldAutoCalculateOnInitialQuoteLoad
    )
      return;
    if (
      !onRiderChange ||
      effectiveCoverAmount == null ||
      !selectedPlan ||
      policyTermForPremium == null
    )
      return;
    if (planChangeInFlightRef.current) return;

    const currentOptionsKey = JSON.stringify({
      premiumRefundEnabled: !!quoteOptionsRedux?.premiumRefundEnabled,
      enhancedCoverageEnabled: !!quoteOptionsRedux?.enhancedCoverageEnabled,
    });
    if (prevQuoteOptionsRef.current == null) {
      prevQuoteOptionsRef.current = currentOptionsKey;
      return;
    }
    if (prevQuoteOptionsRef.current === currentOptionsKey) return;
    prevQuoteOptionsRef.current = currentOptionsKey;

    onRiderChange(
      selectedPlan,
      effectiveCoverAmount,
      policyTermForPremium,
      premiumFrequency,
      paymentTermType,
      selectedLimitedPayTerm,
      Array.from(selectedRiders),
      riderCovers,
    );
  }, [
    quoteOptionsRedux?.premiumRefundEnabled,
    quoteOptionsRedux?.enhancedCoverageEnabled,
    onRiderChange,
    selectedPlan,
    effectiveCoverAmount,
    policyTermForPremium,
    premiumFrequency,
    paymentTermType,
    selectedLimitedPayTerm,
    selectedRiders,
    riderCovers,
    shouldAutoCalculateOnInitialQuoteLoad,
  ]);

  useEffect(() => {
    planChangeInFlightRef.current = false;
  });

  /** Plus caps cover-till-age at 75; clamp selection when switching from Elite or when options shrink. */
  useEffect(() => {
    if (customCoverTillAge !== "" && customCoverTillAge != null) {
      const n = parseInt(String(customCoverTillAge), 10);
      if (!Number.isNaN(n) && n > maxCustomCoverTillAge) {
        setCustomCoverTillAge(String(maxCustomCoverTillAge));
      }
    }

    if (!coverTillAgeOptions.length) return;
    const maxOpt = Math.max(...coverTillAgeOptions);
    if (!customCoverTillAge && selectedCoverTillAge != null) {
      const isInCurrentOptions = coverTillAgeOptions.some(
        (opt) => Number(opt) === Number(selectedCoverTillAge),
      );
      // If persisted/race value doesn't belong to current stepped options
      // (e.g. 75 from Plus while Elite options are 63/68/73/78),
      // snap to current max option so one radio is always selected.
      if (!isInCurrentOptions || selectedCoverTillAge > maxOpt) {
        setSelectedCoverTillAge(maxOpt);
      }
    }
  }, [
    coverTillAgeOptions,
    selectedCoverTillAge,
    customCoverTillAge,
    maxCustomCoverTillAge,
    setSelectedCoverTillAge,
    setCustomCoverTillAge,
  ]);

  useEffect(() => {
    if (!hasRestoredLifeCoverSelectionRef.current) return;
    const parsedStageCoverTillAge = Number(
      quoteStageDataFromJourney?.coverTillAge,
    );
    const parsedQuoteCoverTillAge = Number(quoteResultFromStore?.coverTillAge);
    const hasValidPersistedCoverTillAge =
      (quoteStageDataFromJourney?.customCoverTillAge != null &&
        String(quoteStageDataFromJourney.customCoverTillAge).trim() !== "") ||
      (quoteResultFromStore?.customCoverTillAge != null &&
        String(quoteResultFromStore.customCoverTillAge).trim() !== "") ||
      (!Number.isNaN(parsedStageCoverTillAge) && parsedStageCoverTillAge > 0) ||
      (!Number.isNaN(parsedQuoteCoverTillAge) && parsedQuoteCoverTillAge > 0);
    if (hasValidPersistedCoverTillAge) {
      return;
    }
    if (
      coverTillAgeOptions.length > 0 &&
      !selectedCoverTillAge &&
      !customCoverTillAge
    ) {
      setSelectedCoverTillAge(Math.max(...coverTillAgeOptions));
    }
  }, [
    coverTillAgeOptions,
    selectedCoverTillAge,
    customCoverTillAge,
    quoteStageDataFromJourney,
    quoteResultFromStore,
  ]);

  useEffect(() => {
    const currentAge = customCoverTillAge
      ? parseInt(customCoverTillAge, 10)
      : selectedCoverTillAge;
    if (currentAge == null || Number.isNaN(Number(currentAge))) return;
    if (coverTillToPolicyTerm.has(currentAge)) {
      setSelectedPolicyTerm(coverTillToPolicyTerm.get(currentAge));
      return;
    }
    if (customCoverTillAge && age != null) {
      const calculatedTerm = Number(currentAge) - Number(age);
      if (
        calculatedTerm >= MIN_POLICY_TERM_YEARS &&
        calculatedTerm <= desiredInputMaxPolicyTermCeiling
      ) {
        setSelectedPolicyTerm(calculatedTerm);
      } else {
        setSelectedPolicyTerm(null);
      }
    }
  }, [
    selectedCoverTillAge,
    customCoverTillAge,
    coverTillToPolicyTerm,
    age,
    desiredInputMaxPolicyTermCeiling,
  ]);

  useEffect(() => {
    if (selectedPlan && selectedCoverAmount && policyTermForPremium) {
      onPlanChange(
        selectedPlan,
        premiumFrequency,
        selectedCoverAmount,
        paymentTermType,
        selectedLimitedPayTerm,
        policyTermForPremium,
        true,
      );
    }
  }, [
    selectedPlan,
    selectedCoverAmount,
    policyTermForPremium,
    premiumFrequency,
    paymentTermType,
    selectedLimitedPayTerm,
  ]);

  const handleCustomDesiredCoverBlur = useCallback(() => {
    setIsCustomDesiredCoverFieldFocused(false);
  }, []);

  const handleCustomCoverAmountChange = (e) => {
    markUserEditedQuote();
    const value = sanitizeCoverAmountInputDecimals(e.target.value).trim();
    setCustomCoverAmount(value);
    if (value === "") {
      setCoverAmountError("");
      if (defaultSelectedCoverAmount != null) {
        applyDefaultLifeCoverAmountSelection();
      } else {
        setSelectedCoverAmount(null);
        dispatch(
          journeyActions.setQuoteCoverAmountSelection({
            selectedCoverAmount: null,
            customCoverAmount: "",
          }),
        );
      }
      return;
    }
    // Keep custom input in redux so it can be restored in new tabs.
    dispatch(
      journeyActions.setQuoteCoverAmountSelection({
        selectedCoverAmount: null,
        customCoverAmount: value,
      }),
    );
    setSelectedCoverAmount(null);
    const parsed = parseCoverAmountInput(value);
    if (parsed == null) {
      setCoverAmountError("Enter amount as number (e.g. 24000000 or 2.4 Cr)");
      return;
    }
    if (parsed < effectiveMinCoverAmount) {
      setCoverAmountError(
        `Minimum cover for ${selectedPlan === PLAN_NAMES.ELITE ? "Elite" : "Plus"} is ${formatLakhs(effectiveMinCoverAmount)}`,
      );
      return;
    }
    if (parsed > effectiveMaxCoverAmount) {
      setCoverAmountError(
        `Maximum cover amount which can be entered is ${formatLakhs(effectiveMaxCoverAmount)}`,
      );
      return;
    }
    setCoverAmountError("");
  };

  const handleCustomCoverTillAgeChange = (e) => {
    markUserEditedQuote();
    const value = e.target.value.replace(/\D/g, "");
    setCoverTillAgeError("");
    if (value === "") {
      setCustomCoverTillAge("");
      setSelectedCoverTillAge(null);
      dispatch(
        journeyActions.setQuoteCoverTillAgeSelection({
          selectedCoverTillAge: null,
          customCoverTillAge: "",
        }),
      );
      return;
    }
    const numValue = parseInt(value, 10);
    if (!isNaN(numValue)) {
      // Always allow typing progression (e.g. "4" -> "48"), then validate.
      setCustomCoverTillAge(value);
      setSelectedCoverTillAge(null);
      dispatch(
        journeyActions.setQuoteCoverTillAgeSelection({
          selectedCoverTillAge: null,
          customCoverTillAge: value,
        }),
      );

      if (
        minCoverTillAgeForPolicyTerm != null &&
        numValue < minCoverTillAgeForPolicyTerm
      ) {
        setCoverTillAgeError(
          `Minimum age which can be covered is ${minCoverTillAgeForPolicyTerm} years`,
        );
      } else if (numValue > maxCustomCoverTillAge) {
        setCoverTillAgeError(
          premiumRefundEnabled
            ? `With Return of Premium, maximum cover till age is ${MAX_COVER_TILL_AGE_PLUS} years.`
            : `Cover till age cannot exceed ${maxCustomCoverTillAge} years.`,
        );
      } else {
        setCoverTillAgeError("");
      }
    } else {
      setCustomCoverTillAge(value);
      setSelectedCoverTillAge(null);
      dispatch(
        journeyActions.setQuoteCoverTillAgeSelection({
          selectedCoverTillAge: null,
          customCoverTillAge: value,
        }),
      );
    }
  };

  const validateQuoteSubmit = useCallback(() => {
    if (!applicationNumber || !caseId)
      return "Application number or Case ID is missing. Please go back and complete the lead form.";
    if (!effectiveCoverAmount) return "Please select or enter a cover amount.";
    if (!selectedCoverTillAge && !customCoverTillAge)
      return "Please select or enter cover till age.";
    if (!selectedPolicyTerm) return "Please select a policy term.";
    if (Number(selectedPolicyTerm) < MIN_POLICY_TERM_YEARS) {
      return `Policy term must be at least ${MIN_POLICY_TERM_YEARS} years.`;
    }
    const maxPtCeiling = Math.min(
      MAX_POLICY_TERM_YEARS,
      customCoverTillAge
        ? desiredInputMaxPolicyTermCeiling
        : reconciledMaxPolicyTerm,
    );
    if (Number(selectedPolicyTerm) > maxPtCeiling) {
      return `Policy term cannot exceed ${maxPtCeiling} years.`;
    }
    const riderResolved = resolveRiderCoversForSubmit();
    if (riderResolved.error) return riderResolved.error;
    return null;
  }, [
    applicationNumber,
    caseId,
    effectiveCoverAmount,
    selectedCoverTillAge,
    customCoverTillAge,
    selectedPolicyTerm,
    desiredInputMaxPolicyTermCeiling,
    reconciledMaxPolicyTerm,
    resolveRiderCoversForSubmit,
  ]);

  const buildBISummaryPayload = useCallback(
    (riderCoversOverride) => {
      const rc = riderCoversOverride ?? riderCovers;
      const resolvedDob = leadInput?.lifeAssuredDob || "";
      const ageVal = resolvedDob ? calculateAge(resolvedDob) : null;
      const prSa =
        effectiveCoverAmount ?? leadResult?.maxCoverAmount ?? 50000000;
      const prPt = selectedPolicyTerm != null ? String(selectedPolicyTerm) : "";
      const prPpt =
        paymentTermType === "Limited Pay"
          ? String(selectedLimitedPayTerm)
          : selectedPolicyTerm != null
            ? String(selectedPolicyTerm)
            : "";
      // Use Excel-calculated annualPremium when available so BI API receives the same value as Excel and returns matching ModalPremium
      const derivedAnnualPremium =
        premiumFrequency === "Yearly"
          ? (totalPayableAmount ?? 0)
          : premiumFrequency === "Monthly"
            ? (totalPayableAmount ?? 0) * 12
            : premiumFrequency === "Quarterly"
              ? (totalPayableAmount ?? 0) * 4
              : (totalPayableAmount ?? 0) * 2;
      const annualPremium =
        displayPremiumResult?.annualPremium != null
          ? Number(displayPremiumResult.annualPremium)
          : derivedAnnualPremium;
      const monthlyPremium = annualPremium / 12;
      // Modal premium = premium per payment period (so BI API uses it and does not recalculate)
      const modalPremium =
        premiumFrequency === "Yearly"
          ? annualPremium
          : premiumFrequency === "Monthly"
            ? annualPremium / 12
            : premiumFrequency === "Quarterly"
              ? annualPremium / 4
              : annualPremium / 2;
      const freezeDateObj = new Date();
      const freezeDateStr = `${freezeDateObj.getDate()} ${["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"][freezeDateObj.getMonth()]} ${freezeDateObj.getFullYear()}`;
      const stateVal = getLeadStateForBi(
        leadData,
        editDetailsFormData,
        leadInput,
      );
      const resolvedNationality = getLeadNationality(
        leadData,
        editDetailsFormData,
        leadInput,
      );

      // Riders payload for BI API.
      // Include only selected riders; SUMASSURED uses committed rider cover (ADD+ = life cover).
      const biRiders = Array.from(selectedRiders).map((frontendRiderId) => {
        const biRiderId = BI_RIDER_ID_BY_FRONTEND_ID[frontendRiderId];
        const biRiderName = BI_RIDER_NAME_BY_FRONTEND_ID[frontendRiderId];
        const def = RIDERS_DATA.find((r) => r.id === frontendRiderId);
        const saValue =
          frontendRiderId === RIDER_ID_ADD_PLUS && effectiveCoverAmount != null
            ? effectiveCoverAmount
            : (rc?.[frontendRiderId] ?? def?.minCover ?? 0);

        return {
          inputOptions: [],
          formInputs: [{ key: "@RD_SA", value: String(saValue) }],
          RiderId: biRiderId,
          RiderName: biRiderName,
        };
      });

      return {
        productName: selectedPlan ?? "",
        selectedPlan: selectedPlan ?? "",
        applicationNumber: applicationNumber ?? "",
        caseId: caseId ?? "",
        ecs: "1",
        channel: leadResult?.premiumDefaults?.channel ?? "Online",
        ROP: premiumRefundEnabled ? "Yes" : "",
        enhancedCoverageBenefit: planSupportsEnhancedCoveragePayload
          ? enhancedCoverageEnabled
            ? "Yes"
            : "No"
          : "Doesn't meet eligibility",
        riders: biRiders,
        inputMode: getInputModeFromFrequency(premiumFrequency),
        premiumFrequency: premiumFrequency || "Yearly",
        policyTerm: prPt,
        prPt,
        prPpt,
        maxCoverAmount: leadResult?.maxCoverAmount ?? 50000000,
        prSa: String(prSa),
        sumAssured: String(prSa),
        age: ageVal != null ? String(ageVal) : "",
        entryAge: ageVal != null ? String(ageVal) : "",
        liEntryAge: ageVal != null ? String(ageVal) : "",
        proposerAge: ageVal != null ? String(ageVal) : "",
        name: leadInput?.lifeAssuredName ?? "",
        lifeAssuredName: leadInput?.lifeAssuredName ?? "",
        proposerName: leadInput?.lifeAssuredName ?? "",
        fullName: leadInput?.lifeAssuredName ?? "",
        gender: leadInput?.lifeAssuredGender ?? "",
        liGender: leadInput?.lifeAssuredGender ?? "",
        proposerGender: leadInput?.lifeAssuredGender ?? "",
        state: stateVal,
        liState: stateVal,
        companyState: leadInput?.companyState ?? "0",
        gstin: "",
        gstinNumber: "",
        sameProposer: "True",
        dob: resolvedDob,
        dateOfBirth: resolvedDob,
        proposerDob: resolvedDob,
        liDob: resolvedDob,
        mobile: leadInput?.mobileNumber ?? "",
        mobileNo: leadInput?.mobileNumber ?? "",
        liMobileno: leadInput?.mobileNumber ?? "",
        email: leadInput?.emailId ?? leadInput?.emailAddress ?? "",
        emailId: leadInput?.emailId ?? leadInput?.emailAddress ?? "",
        liEmailid: leadInput?.emailId ?? leadInput?.emailAddress ?? "",
        kfd: "E",
        pincode: leadInput?.pinCode ?? "",
        pinCode: leadInput?.pinCode ?? "",
        prAnnprem: String(Math.round(annualPremium)),
        annualPremium: String(Math.round(annualPremium)),
        prMi: String(Math.round(monthlyPremium)),
        prModalPrem: String(Math.round(modalPremium)),
        modalPrem: String(Math.round(modalPremium)),
        freezeDate: freezeDateStr,
        FreezeDate: freezeDateStr,
        businessTypeId: null,
        occupation: leadInput?.occupationClass ?? "0",
        education: leadInput?.education ?? leadInput?.educationalLevel ?? "",
        occupationClass: "",
        isPdf: "1",
        ageProof: "1",
        prChannel: "7",
        empDiscount: "0",
        annIncome: leadInput?.annualIncome ?? "",
        annualIncome: leadInput?.annualIncome ?? "",
        annualIncomeBiCode: leadInput?.annualIncomeBiCode ?? "",
        annualIncomeRangeLabel: leadInput?.annualIncomeRangeLabel ?? "",
        fundStrategyId: "3",
        prId: resolveBiApiProductId(selectedPlan) ?? null,
        productId: resolveBiApiProductId(selectedPlan) ?? null,
        productCode: resolveBiApiProductId(selectedPlan) ?? null,
        agentId: "",
        // Lead capture uses smokerStatus; proposal may use tobaccoConsumption / smokingHabit — include all for BI @LI_SMOKE.
        smokingHabit:
          leadInput?.tobaccoConsumption ??
          leadInput?.smokingHabit ??
          leadInput?.smokerStatus ??
          "No",
        tobaccoConsumption:
          leadInput?.tobaccoConsumption ??
          leadInput?.smokingHabit ??
          leadInput?.smokerStatus ??
          "No",
        liSmoke:
          leadInput?.tobaccoConsumption ??
          leadInput?.smokingHabit ??
          leadInput?.smokerStatus ??
          "No",
        nationality: resolvedNationality,
        basedInIndia: resolvedNationality,
      };
    },
    [
      leadResult,
      leadInput,
      leadData,
      editDetailsFormData,
      applicationNumber,
      caseId,
      selectedPlan,
      premiumRefundEnabled,
      enhancedCoverageEnabled,
      selectedRiders,
      riderCovers,
      premiumFrequency,
      selectedPolicyTerm,
      paymentTermType,
      selectedLimitedPayTerm,
      effectiveCoverAmount,
      totalPayableAmount,
      displayPremiumResult,
      planSupportsEnhancedCoveragePayload,
    ],
  );

  const buildQuotePayload = useCallback(
    (riderCoversOverride) => {
      const rc = riderCoversOverride ?? riderCovers;
      const coverTillAge =
        (customCoverTillAge !== "" && customCoverTillAge != null
          ? parseInt(String(customCoverTillAge), 10)
          : null) || selectedCoverTillAge;
      const paymentTermTenure =
        paymentTermType === "Limited Pay"
          ? `${selectedLimitedPayTerm} Years`
          : selectedPolicyTerm
            ? `${selectedPolicyTerm} Years`
            : "";
      const firstRiderId =
        selectedRiders.size > 0 ? Array.from(selectedRiders)[0] : null;
      const firstRider = firstRiderId
        ? RIDERS_DATA.find((r) => r.id === firstRiderId)
        : null;

      return {
        applicationNumber,
        caseId,
        product: "Life",
        productName: selectedPlan,
        productCode:
          resolveBiApiProductId(selectedPlan) ??
          (PLAN_TO_PRODUCT_CODE[selectedPlan] ||
            leadResult?.assignedProductCode ||
            ""),
        coverAmount: effectiveCoverAmount,
        maxCoverAmount: leadResult?.maxCoverAmount || maxCoverAmount,
        coverTillAge,
        policyTerm: selectedPolicyTerm,
        paymentTermType,
        paymentTermTenure,
        premiumFrequency,
        premiumPaymentFrequency: premiumFrequency,
        premiumAmount: totalPayableAmount,
        totalPayableAmount,
        finalDiscountedPremium:
          displayPremiumResult?.finalDiscountedPremium || totalPayableAmount,
        finalPremium: displayPremiumResult?.finalPremium || totalPayableAmount,
        premiumSavingsAmount:
          displayPremiumResult?.premiumSavingsAmount || premiumSavingsAmount,
        onlineDiscount: displayPremiumResult?.onlineDiscount || onlineDiscount,
        salariedDiscount:
          displayPremiumResult?.salariedDiscount || salariedDiscount,
        returnOfPremium: premiumRefundEnabled,
        enhancedCoverageBenefit: planSupportsEnhancedCoveragePayload
          ? enhancedCoverageEnabled
            ? "Yes"
            : "No"
          : "Doesn't meet eligibility",
        riderName: firstRider ? firstRider.name : "",
        riderPremiumAmount:
          firstRider && riderPremiumFromApi[firstRiderId] != null
            ? riderPremiumFromApi[firstRiderId]
            : firstRider
              ? 0
              : null,
        ridersCoverAmount:
          firstRider && firstRiderId
            ? firstRiderId === RIDER_ID_ADD_PLUS && effectiveCoverAmount != null
              ? effectiveCoverAmount
              : rc[firstRiderId] || firstRider.minCover
            : 0,
        riderCovers: { ...rc },
        riderPremiumFromApi: { ...riderPremiumFromApi },
        ridersDetail: Array.from(selectedRiders).map((id) => {
          const r = RIDERS_DATA.find((x) => x.id === id);
          const coverAmt =
            id === RIDER_ID_ADD_PLUS && effectiveCoverAmount != null
              ? effectiveCoverAmount
              : (rc[id] ?? r?.minCover ?? null);
          return {
            riderId: id,
            name: r?.name ?? id,
            coverAmount: coverAmt,
            premium: riderPremiumFromApi[id] ?? null,
          };
        }),
        switchedToPlusDueToROP,
        customCoverAmount,
        customCoverTillAge,
        showMoreRiders,
        isEliteEligible,
        premiumResult: cloneForJourney(premiumResult),
        // Persist from local UI state so cross-browser restore uses the latest user selections
        // even if Redux sync effects are still in-flight at submit time.

        quoteRiders: cloneForJourney({
          selectedIds: Array.from(selectedRiders),
          covers: { ...rc },
        }),
        quoteOptions: cloneForJourney({
          premiumRefundEnabled: !!premiumRefundEnabled,
          enhancedCoverageEnabled: !!enhancedCoverageEnabled,
        }),
        selectedRiders: Array.from(selectedRiders),
        riderADBPremium: displayPremiumResult?.riderADBPremium,
        riderADDPremium: displayPremiumResult?.riderADDPremium,
        riderADDPlusPremium: displayPremiumResult?.riderADDPlusPremium,
        riderCIPremium: displayPremiumResult?.riderCIPremium,
        leadInput,
        lifeAssuredName:
          leadInput?.lifeAssuredName || leadInput?.fullName || "",
        lifeAssuredDob:
          leadInput?.lifeAssuredDob || leadInput?.dateOfBirth || "",
        lifeAssuredAge: calculateAge(
          leadInput?.lifeAssuredDob || leadInput?.dateOfBirth,
        ),
        gender: leadInput?.gender || "",
        mobileNumber: leadInput?.mobileNumber || "",
        emailAddress: leadInput?.emailAddress || "",
        annualIncome: leadInput?.annualIncome || "",
        occupation: leadInput?.occupation || "",
        eligibilityStatus: leadResult?.eligibilityStatus || "Eligible",
        assignedProductName: leadResult?.assignedProductName || "",
        coverTillAgeOptions: leadResult?.coverTillAgeOptions,
        policyTermOptions: leadResult?.policyTermOptions,
        quoteStage: "Success",
      };
    },
    [
      applicationNumber,
      caseId,
      leadResult,
      leadInput,
      selectedPlan,
      effectiveCoverAmount,
      maxCoverAmount,
      selectedCoverTillAge,
      customCoverTillAge,
      selectedPolicyTerm,
      paymentTermType,
      selectedLimitedPayTerm,
      premiumFrequency,
      totalPayableAmount,
      displayPremiumResult,
      premiumSavingsAmount,
      onlineDiscount,
      salariedDiscount,
      premiumRefundEnabled,
      enhancedCoverageEnabled,
      selectedRiders,
      riderCovers,
      riderPremiumFromApi,
      switchedToPlusDueToROP,
      customCoverAmount,
      customCoverTillAge,
      showMoreRiders,
      isEliteEligible,
      canOfferEliteAtPincode,
      planSupportsEnhancedCoveragePayload,
      premiumResult,
    ],
  );

  const handleEnhancedCoverageToggle = useCallback(
    (e) => {
      const checked = e.target.checked;
      markUserEditedQuote();
      if (checked) {
        setPremiumRefundEnabled(false);
        if (selectedRiders.has(RIDER_ID_ADD_PLUS)) {
          toggleRider(RIDER_ID_ADD_PLUS);
        }
      }
      setEnhancedCoverageEnabled(checked);
    },
    [
      markUserEditedQuote,
      selectedRiders,
      toggleRider,
      setPremiumRefundEnabled,
      setEnhancedCoverageEnabled,
    ],
  );

  const handlePlanSelect = useCallback(
    (plan) => {
      if (
        isLeadGuidedFlowActive &&
        activeGuidedStep !== GUIDED_QUOTE_STEPS.PLAN
      ) {
        return;
      }
      markUserEditedQuote();
      if (plan === PLAN_NAMES.ELITE && !canOfferEliteAtPincode) {
        setShowPlanIneligibleModal(true);
        return;
      }
      if (plan === PLAN_NAMES.PLUS && !pincodeAllowsPlus) {
        setShowPlanIneligibleModal(true);
        return;
      }
      planChangeInFlightRef.current = true;
      prevDimensionsRef.current = null;
      setSwitchedToPlusDueToROP(false);
      setSelectedPlan(plan);
      if (!isLeadGuidedFlowActive) {
        onPlanChange?.(
          plan,
          premiumFrequency,
          effectiveCoverAmount,
          paymentTermType,
          selectedLimitedPayTerm,
          resolvePolicyTermForApiForPlan(plan),
          true,
        );
      }
    },
    [
      canOfferEliteAtPincode,
      pincodeAllowsPlus,
      setSelectedPlan,
      setSwitchedToPlusDueToROP,
      setShowPlanIneligibleModal,
      onPlanChange,
      premiumFrequency,
      effectiveCoverAmount,
      paymentTermType,
      selectedLimitedPayTerm,
      resolvePolicyTermForApiForPlan,
      markUserEditedQuote,
      isLeadGuidedFlowActive,
      activeGuidedStep,
    ],
  );

  const handleSubmitQuote = useCallback(async () => {
    const validationError = validateQuoteSubmit();
    if (validationError) {
      setSubmitError(validationError);
      return;
    }

    const resolved = resolveRiderCoversForSubmit();
    if (resolved.error || !resolved.covers) {
      setSubmitError(resolved.error || "Please fix add-on cover amounts.");
      return;
    }
    const patch = {};
    for (const id of selectedRiders) {
      if (resolved.covers[id] !== riderCovers[id]) {
        patch[id] = resolved.covers[id];
      }
    }
    if (Object.keys(patch).length > 0) {
      syncRiderCovers(patch);
    }
    const coversForPayload = resolved.covers;

    setSubmitting(true);
    setSubmitError(null);
    try {
      const redirected = await redirectToLatestJourneyStage({
        applicationNumber,
        dispatch,
        navigate,
        getState: store.getState,
        currentPath: "/quote",
      });
      if (redirected) return;

      const quoteData = buildQuotePayload(coversForPayload);
      const biPayload = buildBISummaryPayload(coversForPayload);
      const result = await submitQuote(
        { ...quoteData, biPayload },
        applicationNumber,
        caseId,
      );
      const biResult = result?.biResult || {};
      const biStatus = String(biResult?.Status || "")
        .trim()
        .toLowerCase();
      const isBiFailed =
        (result?.biGenerated === true && biStatus === "fail") ||
        result?.biGenerated === false;
      if (isBiFailed) {
        setBiFailureMessage(BI_FAILURE_MESSAGE);
        setShowBiFailureModal(true);
        return;
      }

      if (onComplete) {
        const pdfUrl =
          biResult?.response?.PDFURL ||
          biResult?.PDFURL ||
          biResult?.pdfUrl ||
          biResult?.pdfURL ||
          result?.PDFURL ||
          result?.pdfUrl ||
          result?.pdfURL ||
          result?.biPdfUrl ||
          null;
        onComplete({
          result,
          quoteData: {
            ...quoteData,
            totalPayableAmount,
            premiumFrequency,
            selectedRiders: Array.from(selectedRiders),
            riderCovers: coversForPayload,
            pdfUrl,
          },
        });
      } else {
        setShowSuccessModal(true);
      }
    } catch (err) {
      logError("[Quote] Quote submission failed", err);
      setSubmitError(
        err?.response?.data?.message ||
          err?.message ||
          "Failed to submit quote. Please try again.",
      );
    } finally {
      setSubmitting(false);
    }
  }, [
    validateQuoteSubmit,
    resolveRiderCoversForSubmit,
    syncRiderCovers,
    buildQuotePayload,
    buildBISummaryPayload,
    selectedPlan,
    applicationNumber,
    caseId,
    onComplete,
    totalPayableAmount,
    premiumFrequency,
    selectedRiders,
    riderCovers,
    dispatch,
    navigate,
    store,
  ]);

  const isPayButtonDisabled =
    submitting ||
    !applicationNumber ||
    !caseId ||
    hasRiderInputErrors ||
    Boolean(coverAmountError) ||
    Boolean(coverTillAgeError) ||
    (String(customCoverTillAge || "").trim() !== "" &&
      (() => {
        const n = parseInt(String(customCoverTillAge), 10);
        if (Number.isNaN(n)) return true;
        const minAllowed = minCoverTillAgeForPolicyTerm ?? 1;
        return n < minAllowed || n > maxCustomCoverTillAge;
      })());

  /** Initial / resume quote: block full page only until primary premium + quote init. */
  const isInitialPremiumFlowBlocking =
    !hasUserEditedQuoteRef.current && shouldAutoCalculateOnInitialQuoteLoad;

  const shouldShowQuoteLoader =
    !leadResult ||
    quoteInitLoading ||
    (isInitialPremiumFlowBlocking &&
      !premiumError &&
      (!displayPremiumResult || premiumLoading));

  /** After primary premium is visible, suppress the overlay during background prefetch. */
  const suppressPremiumOverlayForInitialGridPrefetchOnly =
    isInitialPremiumFlowBlocking &&
    displayPremiumResult &&
    !premiumLoading &&
    !quoteInitLoading;

  /** Any in-flight premium work (after first paint): single overlay, no stacking with options-only blur. */
  const isPremiumCalculationBusy =
    premiumLoading || quoteInitLoading || guidedBulkLoading;
  const shouldShowPremiumLoader =
    submitting ||
    guidedBulkLoading ||
    (isPremiumCalculationBusy &&
      !suppressPremiumOverlayForInitialGridPrefetchOnly &&
      (!isCustomDesiredCoverFieldFocused || guidedBulkLoading));

  if (shouldShowQuoteLoader) {
    return (
      <div
        className="quote-premium-loader-overlay"
        aria-live="polite"
        aria-busy="true"
      >
        <BrandLoader color="#97262A" loading={true} size={15} />
      </div>
    );
  }

  return (
    <div className="term-insurance-journey-quote">
      <header className="quote-header">
        <div className="quote-header-wraper">
          <div className="header-left">
            {/* <button
              className="back-button"
              onClick={onBack}
              aria-label="Go back"
            >
              <svg
                width="18"
                height="14"
                viewBox="0 0 18 14"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M17 7H1M1 7L7 13M1 7L7 1"
                  stroke="#252527"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            </button> */}
            <h6 className="header-title m-0">Let's Customise your plan</h6>
          </div>
          <button
            className="help-button btn secondary-button regular"
            onClick={() => setShowHelpModal(true)}
          >
            <svg
              width="20"
              height="21"
              viewBox="0 0 20 21"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M16.6772 2.87806C15.3151 1.50799 13.5769 0.573101 11.683 0.191867C9.789 -0.189367 7.82449 0.000214264 6.03838 0.736587C4.25227 1.47296 2.72496 2.72298 1.64998 4.32823C0.575006 5.93349 0.000755732 7.82174 0 9.75369V15.0037C0 15.6004 0.237053 16.1727 0.65901 16.5947C1.08097 17.0166 1.65326 17.2537 2.25 17.2537H3.75C4.34674 17.2537 4.91903 17.0166 5.34099 16.5947C5.76295 16.1727 6 15.6004 6 15.0037V11.2537C6 10.657 5.76295 10.0847 5.34099 9.6627C4.91903 9.24074 4.34674 9.00369 3.75 9.00369H1.53375C1.67839 7.43988 2.26578 5.94969 3.22709 4.70779C4.18839 3.46588 5.48377 2.52372 6.96141 1.99172C8.43904 1.45972 10.0377 1.35993 11.57 1.70404C13.1024 2.04815 14.5048 2.82191 15.6131 3.93463C16.9677 5.29616 17.8009 7.09031 17.9672 9.00369H15.75C15.1533 9.00369 14.581 9.24074 14.159 9.6627C13.7371 10.0847 13.5 10.657 13.5 11.2537V15.0037C13.5 15.6004 13.7371 16.1727 14.159 16.5947C14.581 17.0166 15.1533 17.2537 15.75 17.2537H18C18 17.8504 17.7629 18.4227 17.341 18.8447C16.919 19.2666 16.3467 19.5037 15.75 19.5037H10.5C10.3011 19.5037 10.1103 19.5827 9.96967 19.7234C9.82902 19.864 9.75 20.0548 9.75 20.2537C9.75 20.4526 9.82902 20.6434 9.96967 20.784C10.1103 20.9247 10.3011 21.0037 10.5 21.0037H15.75C16.7446 21.0037 17.6984 20.6086 18.4017 19.9053C19.1049 19.2021 19.5 18.2483 19.5 17.2537V9.75369C19.5049 8.47829 19.2581 7.21446 18.7737 6.03462C18.2893 4.85477 17.5768 3.7821 16.6772 2.87806ZM3.75 10.5037C3.94891 10.5037 4.13968 10.5827 4.28033 10.7234C4.42098 10.864 4.5 11.0548 4.5 11.2537V15.0037C4.5 15.2026 4.42098 15.3934 4.28033 15.534C4.13968 15.6747 3.94891 15.7537 3.75 15.7537H2.25C2.05109 15.7537 1.86032 15.6747 1.71967 15.534C1.57902 15.3934 1.5 15.2026 1.5 15.0037V10.5037H3.75ZM15.75 15.7537C15.5511 15.7537 15.3603 15.6747 15.2197 15.534C15.079 15.3934 15 15.2026 15 15.0037V11.2537C15 11.0548 15.079 10.864 15.2197 10.7234C15.3603 10.5827 15.5511 10.5037 15.75 10.5037H18V15.7537H15.75Z"
                fill="#97262A"
              />
            </svg>
            Need Help?
          </button>
        </div>
      </header>

      <section className="plan-banner">
        <div className="plan-wraper">
          <div className="plan-banner-left">
            <h6 className="plan-banner-label m-0">Protection Plan</h6>
            <h2 className="plan-banner-title bold">
              {TEXT_CONTENT.BANNER_HEADING}
            </h2>
          </div>
          <div className="plan-banner-right">
            <div className="user-info">
              <span>{lifeAssuredName}</span> <span>|</span>{" "}
              <span>
                {age} years ({formattedDob})
              </span>
            </div>
            <button
              className="edit-details-button "
              onClick={() => setShowEditDetailsModal(true)}
            >
              <span>Show Details</span>
              <svg
                width="24"
                height="24"
                fill="none"
                viewBox="0 0 24 24"
                className="toolbelt_banner--viewOnlyIcon--KcDPB"
              >
                <path
                  fill="white"
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M6 12c0-.066.054-.358.313-.825a5.9 5.9 0 0 1 1.12-1.414C8.443 8.816 9.956 8 12 8s3.558.816 4.566 1.76c.508.477.88.98 1.121 1.415.258.467.313.76.313.825 0 .066-.055.358-.313.825-.24.435-.613.938-1.12 1.414C15.557 15.184 14.044 16 12 16s-3.558-.816-4.566-1.76a5.9 5.9 0 0 1-1.121-1.415C6.055 12.358 6 12.065 6 12m-1 0c0-1.25 2.333-5 7-5s7 3.75 7 5-2.333 5-7 5-7-3.75-7-5m8 0a1 1 0 1 1-2 0 1 1 0 0 1 2 0m1 0a2 2 0 1 1-4 0 2 2 0 0 1 4 0"
                />
              </svg>
            </button>
          </div>
        </div>
      </section>

      {/* {isLeadGuidedFlowActive && <div className="quote-guided-backdrop" />} */}
      {isLeadGuidedFlowActive && guidedHintText && guidedHintCoords && (
        <div
          className={`quote-guided-hint ${
            guidedHintPlaceAbove
              ? "quote-guided-hint--above"
              : "quote-guided-hint--below"
          }`}
          style={{
            top: guidedHintCoords.top,
            left: guidedHintCoords.left,
          }}
        >
          {!guidedHintPlaceAbove && <GuidedHintArrow direction="up" />}
          <p className="quote-guided-hint__text">{guidedHintText}</p>
          {isActiveGuidedStepReady && (
            <button
              type="button"
              className="quote-guided-hint__next btn white-button regular"
              onClick={handleGuidedNextClick}
            >
              Next
            </button>
          )}
          {guidedHintPlaceAbove && <GuidedHintArrow direction="down" />}
        </div>
      )}
      <section className="plan-and-summary-section">
        <div className="plan-and-summary-wraper">
          <div className="plan-selection-section">
            <div
              className={`plan-options ${
                isLeadGuidedFlowActive &&
                activeGuidedStep === GUIDED_QUOTE_STEPS.PLAN
                  ? "quote-guided-target quote-guided-target--active"
                  : ""
              }`}
            >
              {visibleTermPlans.map((plan) => (
                <div
                  key={plan}
                  className={`plan-option ${selectedPlan === plan ? "selected" : ""} ${
                    plan === PLAN_NAMES.ELITE && !canOfferEliteAtPincode
                      ? "disabled"
                      : ""
                  } ${
                    plan === PLAN_NAMES.PLUS && !pincodeAllowsPlus
                      ? "disabled"
                      : ""
                  }`}
                  onClick={() => handlePlanSelect(plan)}
                >
                  <div className="radio-button">
                    <input
                      type="radio"
                      name="plan"
                      checked={selectedPlan === plan}
                      readOnly
                    />
                  </div>
                  <div className="plan-option-content">
                    <h6 className="plan-option-title m-0">
                      Super Suraksha{" "}
                      <span>
                        {plan.includes("Plus") ? "Plus" : "Elite"}
                      </span>{" "}
                    </h6>
                  </div>
                </div>
              ))}
            </div>

            <div className="plans-detail-wraper">
              <div
                className={`cover-amount-section card-plan-list ${
                  isLeadGuidedFlowActive &&
                  activeGuidedStep === GUIDED_QUOTE_STEPS.LIFE_COVER
                    ? "quote-guided-target quote-guided-target--active"
                    : ""
                }`}
              >
                <div className="nudge-text">
                  People in your age group choose maximum coverage for maximum
                  financial security
                </div>
                <div className="section-header">
                  <div className="header-top">
                    <h6 className="section-title m-0">Life Cover</h6>
                    {/* <p className="tag-text">Information Text</p> */}
                    <div className="tooltip-container">
                      <InfoCircleIcon className="info-icon" />
                      <span className="tooltip-text">
                        {TEXT_CONTENT.TOOLTIP_TEXT}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="cover-amount-options">
                  {coverAmountOptions.map((option) => (
                    <label
                      key={option.value}
                      className={`cover-amount-option ${selectedCoverAmount === option.value ? "selected" : ""}`}
                    >
                      <span className="input-text">
                        <input
                          type="radio"
                          name="coverAmount"
                          value={option.value}
                          checked={selectedCoverAmount === option.value}
                          onChange={(e) => {
                            if (
                              isLeadGuidedFlowActive &&
                              activeGuidedStep !== GUIDED_QUOTE_STEPS.LIFE_COVER
                            ) {
                              return;
                            }
                            markUserEditedQuote();
                            setSelectedCoverAmount(
                              parseInt(e.target.value, 10),
                            );
                            setCustomCoverAmount("");
                            setCoverAmountError("");
                            dispatch(
                              journeyActions.setQuoteCoverAmountSelection({
                                selectedCoverAmount: parseInt(
                                  e.target.value,
                                  10,
                                ),
                                customCoverAmount: "",
                              }),
                            );
                          }}
                        />
                        <span>{option.label}</span>
                      </span>
                    </label>
                  ))}
                  <div
                    className={`custom-cover-amount-option ${isCustomCoverAmountSelected ? "selected" : ""}`}
                  >
                    <input
                      type="text"
                      inputMode="numeric"
                      className="custom-cover-input"
                      placeholder="Choose Desired"
                      value={desiredCoverInputValue}
                      onChange={handleCustomCoverAmountChange}
                      onFocus={() => {
                        setIsCustomDesiredCoverFieldFocused(true);
                        setSelectedCoverAmount(null);
                        setCoverAmountError("");
                        dispatch(
                          journeyActions.setQuoteCoverAmountSelection({
                            selectedCoverAmount: null,
                            customCoverAmount: "",
                          }),
                        );
                      }}
                      onBlur={handleCustomDesiredCoverBlur}
                    />
                  </div>
                </div>
                {coverAmountError && (
                  <div className="error-message">{coverAmountError}</div>
                )}
                {displayCoverAmount && !coverAmountError && (
                  <div className="selected-cover-display">
                    Selected Cover Amount: {displayCoverAmount}
                  </div>
                )}
              </div>

              <div
                className={`cover-till-age-section card-plan-list ${
                  isLeadGuidedFlowActive &&
                  activeGuidedStep === GUIDED_QUOTE_STEPS.COVER_TILL_AGE
                    ? "quote-guided-target quote-guided-target--active"
                    : ""
                }`}
              >
                <div className="nudge-text">{TEXT_CONTENT.COVER_AGE}</div>
                <div className="section-header">
                  <div className="header-top">
                    <h6 className="section-title m-0">Cover Till Age</h6>
                    {selectedPolicyTerm && (
                      <p className="tag-text">
                        Policy Term: <span>{selectedPolicyTerm} years</span>
                      </p>
                    )}
                  </div>
                </div>
                <div className="cover-till-age-options">
                  {coverTillAgeOptions.map((ageOpt) => (
                    <label
                      key={ageOpt}
                      className={`cover-till-age-option ${selectedCoverTillAge === ageOpt ? "selected" : ""}`}
                    >
                      <span className="input-text">
                        <input
                          type="radio"
                          name="coverTillAge"
                          value={ageOpt}
                          checked={selectedCoverTillAge === ageOpt}
                          onChange={(e) => {
                            if (
                              isLeadGuidedFlowActive &&
                              activeGuidedStep !==
                                GUIDED_QUOTE_STEPS.COVER_TILL_AGE
                            ) {
                              return;
                            }
                            markUserEditedQuote();
                            setSelectedCoverTillAge(
                              parseInt(e.target.value, 10),
                            );
                            setCustomCoverTillAge("");
                            setCoverTillAgeError("");
                            dispatch(
                              journeyActions.setQuoteCoverTillAgeSelection({
                                selectedCoverTillAge: parseInt(
                                  e.target.value,
                                  10,
                                ),
                                customCoverTillAge: "",
                              }),
                            );
                          }}
                        />
                        <span className="cover-till-age-age">
                          {ageOpt} Years
                        </span>
                      </span>
                      <span className="recomanded-text">
                        {(() => {
                          const p = getPremiumForCoverTillAgeOption(ageOpt);
                          if (p != null) {
                            return `${formatCurrency(p)} / ${getDisplayFrequencyLabel(premiumFrequency)}`;
                          }
                          return "—";
                        })()}
                      </span>
                    </label>
                  ))}
                  <div
                    className={`custom-cover-till-age-option ${isCustomCoverTillAgeSelected ? "selected" : ""}`}
                  >
                    <span className="input-text">
                      <input
                        type="text"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        className="custom-cover-till-age-input"
                        placeholder="Choose Desired"
                        value={customCoverTillAge}
                        onChange={handleCustomCoverTillAgeChange}
                        onFocus={() => {
                          setSelectedCoverTillAge(null);
                          dispatch(
                            journeyActions.setQuoteCoverTillAgeSelection({
                              selectedCoverTillAge: null,
                              customCoverTillAge,
                            }),
                          );
                        }}
                        maxLength={3}
                      />
                      {customCoverTillAge && (
                        <span className="cover-till-age-age"> Years</span>
                      )}
                    </span>
                    {customCoverTillAge !== "" && (
                      <span className="recomanded-text">
                        {(() => {
                          if (customCoverTillAgePremium != null) {
                            return `${formatCurrency(customCoverTillAgePremium)} / ${getDisplayFrequencyLabel(premiumFrequency)}`;
                          }
                          return null;
                        })()}
                      </span>
                    )}
                  </div>
                </div>
                {coverTillAgeError && (
                  <div className="error-message">{coverTillAgeError}</div>
                )}
                {displayCoverTillAge && !coverTillAgeError && (
                  <div className="selected-cover-till-age-display">
                    Selected Cover Till Age: {displayCoverTillAge}
                  </div>
                )}
              </div>

              <div
                className={`payment-term-section card-plan-list ${
                  isLeadGuidedFlowActive &&
                  activeGuidedStep === GUIDED_QUOTE_STEPS.PAYMENT_TERM
                    ? "quote-guided-target quote-guided-target--active"
                    : ""
                }`}
              >
                <div className="section-header">
                  <div className="header-top">
                    <h6 className="section-title m-0">Premium Payment Term</h6>
                  </div>
                </div>
                <div className="payment-term-options">
                  <div className="payment-terms-types">
                    <div
                      className={`payment-term-type ${paymentTermType === "Regular Pay" ? "selected" : ""}`}
                    >
                      <label className="payment-term-radio">
                        <input
                          type="radio"
                          name="paymentTermType"
                          value="Regular Pay"
                          checked={paymentTermType === "Regular Pay"}
                          onChange={(_e) => {
                            if (
                              isLeadGuidedFlowActive &&
                              activeGuidedStep !==
                                GUIDED_QUOTE_STEPS.PAYMENT_TERM
                            ) {
                              return;
                            }
                            markUserEditedQuote();
                            setPaymentTermType("Regular Pay");
                          }}
                        />
                        <span>Regular Pay</span>
                      </label>
                      {/* {paymentTermType === "Regular Pay" && ( */}
                      <div className="regular-pay-option">
                        <div className="payment-term-card">
                          <div className="payment-term-duration">
                            For {regularPayPolicyTerm} Years
                          </div>
                          <div className="payment-term-body">
                            <div className="payment-term-premium">
                              {formatCurrency(
                                regularPayPremium ?? finalDiscountedPremium,
                              )}
                            </div>
                            <div className="payment-term-age">
                              Pay till age{" "}
                              <span>{currentCoverTillAge || 60}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                      {/* )} */}
                    </div>
                    <div
                      className={`payment-term-type ${paymentTermType === "Limited Pay" ? "selected" : ""}`}
                    >
                      <label className="payment-term-radio l-pay">
                        <input
                          type="radio"
                          name="paymentTermType"
                          value="Limited Pay"
                          checked={paymentTermType === "Limited Pay"}
                          onChange={() => {
                            if (
                              isLeadGuidedFlowActive &&
                              activeGuidedStep !==
                                GUIDED_QUOTE_STEPS.PAYMENT_TERM
                            ) {
                              return;
                            }
                            markUserEditedQuote();
                            setPaymentTermType("Limited Pay");
                            setSelectedLimitedPayTerm(
                              limitedPayOptions[0] ?? 5,
                            );
                          }}
                        />
                        <span>Limited Pay</span>
                      </label>
                      {/* {paymentTermType === "Limited Pay" && ( */}
                      <div className="limited-pay-slider r-pay">
                        {showLimitedPaySliderArrows && (
                          <button
                            type="button"
                            className="limited-pay-slider__arrow limited-pay-slider__arrow--left"
                            onClick={() => scrollLimitedPayOptions("left")}
                            disabled={!limitedPayScrollArrows.canScrollLeft}
                            aria-label="Scroll limited pay options left"
                          >
                            <BackArrowIcon size={18} />
                          </button>
                        )}
                        <div
                          className="limited-pay-options"
                          ref={limitedPayOptionsScrollRef}
                          onScroll={updateLimitedPayScrollArrows}
                        >
                          {displayLimitedPayOptions.map((years) => {
                            const limitedPaySavingsAmount =
                              getLimitedPaySavingsForTerm(years);
                            const savingsPercentage =
                              calculateSavingsPercentage({
                                savingAmount: limitedPaySavingsAmount,
                                regularPayPremium:
                                  regularPayPremium ?? finalDiscountedPremium,
                                policyTerm: regularPayPolicyTerm,
                                frequency: frequencyToUse,
                              });
                            return (
                              <div
                                key={years}
                                className={`payment-term-card ${paymentTermType === "Limited Pay" && selectedLimitedPayTerm === years ? "selected" : ""}`}
                                onClick={() => {
                                  if (
                                    isLeadGuidedFlowActive &&
                                    activeGuidedStep !==
                                      GUIDED_QUOTE_STEPS.PAYMENT_TERM
                                  ) {
                                    return;
                                  }
                                  markUserEditedQuote();
                                  setPaymentTermType("Limited Pay");
                                  setSelectedLimitedPayTerm(years);
                                }}
                              >
                                <label className="payment-term-radio">
                                  <input
                                    type="radio"
                                    name="limitedPayTerm"
                                    checked={
                                      paymentTermType === "Limited Pay" &&
                                      selectedLimitedPayTerm === years
                                    }
                                    onChange={() => {
                                      if (
                                        isLeadGuidedFlowActive &&
                                        activeGuidedStep !==
                                          GUIDED_QUOTE_STEPS.PAYMENT_TERM
                                      ) {
                                        return;
                                      }
                                      markUserEditedQuote();
                                      setPaymentTermType("Limited Pay");
                                      setSelectedLimitedPayTerm(years);
                                    }}
                                  />
                                  <span className="light-text">
                                    For{" "}
                                    <span className="bold-text">
                                      {years} Years
                                    </span>
                                  </span>
                                </label>
                                <div className="payment-term-body-text">
                                  <div className="payment-term-premium">
                                    {formatCurrency(
                                      getLimitedPayPremiumForTerm(years) ??
                                        finalDiscountedPremium,
                                    )}
                                  </div>
                                  <div className="payment-term-age">
                                    Pay till age {age ? age + years : years}
                                  </div>
                                </div>
                                {limitedPaySavingsAmount > 0 && (
                                  <div className={`payment-term-savings`}>
                                    Save{" "}
                                    {formatCurrency(limitedPaySavingsAmount)} (
                                    {Number.isFinite(savingsPercentage)
                                      ? Math.round(savingsPercentage)
                                      : 0}
                                    %)
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                        {showLimitedPaySliderArrows && (
                          <button
                            type="button"
                            className="limited-pay-slider__arrow limited-pay-slider__arrow--right"
                            onClick={() => scrollLimitedPayOptions("right")}
                            disabled={!limitedPayScrollArrows.canScrollRight}
                            aria-label="Scroll limited pay options right"
                          >
                            <BackArrowIcon
                              size={18}
                              style={{ transform: "rotate(180deg)" }}
                            />
                          </button>
                        )}
                      </div>
                      {/*  )} */}
                      <div className="nudge-text-small">
                        {TEXT_CONTENT.PRE_PAY_TERM}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="premium-refund-section card-plan-list">
                <div className="section-header">
                  <h6 className="section-title m-0">
                    Return of Premium Option
                  </h6>
                  <HoverTooltip content={ropToggleDisabledReason}>
                    <div className="toggle-switch-hover-wrap">
                      <div
                        className={`toggle-switch${ropToggleDisabledReason ? " toggle-switch--disabled" : ""}`}
                      >
                        <input
                          type="checkbox"
                          id="premiumRefund"
                          checked={premiumRefundEnabled}
                          disabled={!!ropToggleDisabledReason}
                          onChange={(e) => {
                            markUserEditedQuote();
                            const next = e.target.checked;
                            if (next) {
                              setEnhancedCoverageEnabled(false);
                            }
                            setPremiumRefundEnabled(next);
                          }}
                        />
                        <label htmlFor="premiumRefund" className="toggle-label">
                          <span className="toggle-text yes">Yes</span>
                          <span className="toggle-text no">No</span>
                        </label>
                      </div>
                    </div>
                  </HoverTooltip>
                </div>
                <p className="section-description">
                  {TEXT_CONTENT.SECTION_DESCRI}
                </p>
                <button
                  className="know-more-link btn tertiary-button small p-0"
                  onClick={() => setShowPremiumRefundModal(true)}
                >
                  Know More
                </button>
              </div>

              {showEnhancedCoverageSection && (
                <div className="enhanced-coverage-section card-plan-list">
                  <div className="section-header">
                    <h6 className="section-title m-0">
                      Enhanced Coverage Benefit Option
                    </h6>
                    <HoverTooltip content={ecboToggleDisabledReason}>
                      <div className="toggle-switch-hover-wrap">
                        <div
                          className={`toggle-switch${ecboToggleDisabledReason ? " toggle-switch--disabled" : ""}`}
                        >
                          <input
                            type="checkbox"
                            id="enhancedCoverage"
                            checked={enhancedCoverageEnabled}
                            disabled={!!ecboToggleDisabledReason}
                            onChange={handleEnhancedCoverageToggle}
                          />

                          <label
                            htmlFor="enhancedCoverage"
                            className="toggle-label"
                          >
                            <span className="toggle-text yes">Yes</span>
                            <span className="toggle-text no">No</span>
                          </label>
                        </div>
                      </div>
                    </HoverTooltip>
                  </div>
                  <p className="section-description">
                    {TEXT_CONTENT.SECTION_DESCRIPTION}
                  </p>
                  <button
                    className="know-more-link btn tertiary-button small p-0"
                    onClick={() => setShowEnhancedCoverageModal(true)}
                  >
                    Know More
                  </button>
                </div>
              )}

              <QuoteRidersSection
                ridersData={RIDERS_DATA}
                selectedRiders={selectedRiders}
                enhancedCoverageEnabled={enhancedCoverageEnabled}
                effectiveCoverAmount={effectiveCoverAmount}
                riderCovers={riderCovers}
                riderCoverDrafts={riderCoverDrafts}
                riderCoverErrors={riderCoverErrors}
                premiumFrequency={premiumFrequency}
                getRiderPremium={getRiderPremium}
                markUserEditedQuote={markUserEditedQuote}
                toggleRider={toggleRider}
                onCoverInputChange={handleRiderCoverInputChange}
                onCoverInputBlur={handleRiderCoverInputBlur}
              />
            </div>
          </div>
          <aside className="aside-section">
            {!isLeadGuidedFlowActive && (
              <div
                className="premium-summary-section"
                aria-label="Premium Breakdown"
              >
                <div className="premium-summary-amount">
                  <div className="premium-summary-main">
                    {formatCurrency(totalPayableAmount)}{" "}
                    <span className="premium-summary-period">
                      / {getDisplayFrequencyLabel(premiumFrequency)}
                    </span>
                  </div>
                  <div className="premium-summary-label">
                    Total Payable Amount
                  </div>
                  {formatCurrency(premiumSavingsAmount) !== "₹ 0" && (
                    <div className="premium-summary-savings">
                      You save{" "}
                      <span>{formatCurrency(premiumSavingsAmount)}</span> on
                      this plan
                    </div>
                  )}
                </div>

                <div className="premium-summary-breakdown">
                  <h6 className="premium-summary-breakdown-title large-body semibold">
                    Premium Breakdown
                  </h6>
                  <div className="premium-summary-row-plan">
                    <div className="premium-summary-row-label">
                      Plan Details
                    </div>
                    <div className="premium-summary-row-plan-text">
                      {PLAN_DISPLAY_NAMES[selectedPlan] ||
                        assignedProductName ||
                        selectedPlan}
                    </div>
                    <div className="premium-summary-row-both">
                      <div className="premium-summary-row-term">
                        <div className="premium-summary-row-label">Term:</div>
                        <div className="premium-summary-row-value">
                          {selectedPolicyTerm
                            ? `${selectedPolicyTerm} Years`
                            : "--"}
                        </div>
                      </div>
                      <span className="divider">|</span>
                      <div className="premium-summary-row-cover">
                        <div className="premium-summary-row-label">
                          Cover Amount:
                        </div>
                        <div className="premium-summary-row-value">
                          {displayCoverAmount
                            ? formatCurrency(effectiveCoverAmount)
                            : "--"}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="premium-summary-row-payment">
                    <div className="premium-summary-row-label">
                      Payment Term
                    </div>
                    <div className="premium-summary-row-value">
                      {paymentTermLabel}
                    </div>
                  </div>
                  <div className="premium-summary-addons">
                    <div className="premium-summary-addons-header">Add Ons</div>
                    <div className="premium-summary-addonsPlans">
                      {selectedRidersList.length === 0 && (
                        <div className="premium-summary-addon-row--empty">
                          No add-ons selected!
                        </div>
                      )}
                      {selectedRidersList.map((rider) => (
                        <div
                          key={rider.id}
                          className="premium-summary-addon-row"
                        >
                          <div className="premium-summary-addon-main">
                            <span className="premium-summary-addon-name">
                              {rider.name}
                            </span>
                            <button
                              type="button"
                              className="premium-summary-addon-remove"
                              onClick={() => {
                                markUserEditedQuote();
                                toggleRider(rider.id);
                              }}
                              aria-label={`Remove ${rider.name} add-on`}
                            >
                              <svg
                                width="14"
                                height="14"
                                viewBox="0 0 14 14"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <path
                                  d="M4.59961 0.601562L8.59961 0.601562M0.599609 2.60156L12.5996 2.60156M11.2663 2.60156L10.7988 9.61442C10.7286 10.6666 10.6935 11.1927 10.4663 11.5916C10.2662 11.9428 9.96444 12.2251 9.60072 12.4014C9.18759 12.6016 8.66034 12.6016 7.60584 12.6016L5.59338 12.6016C4.53888 12.6016 4.01163 12.6016 3.5985 12.4014C3.23478 12.2251 2.933 11.9428 2.73293 11.5916C2.50568 11.1927 2.47061 10.6666 2.40047 9.61442L1.93294 2.60156M5.26628 5.60156L5.26628 8.9349M7.93294 5.60156L7.93294 8.9349"
                                  stroke="#FF3939"
                                  stroke-width="1.2"
                                  stroke-linecap="round"
                                  stroke-linejoin="round"
                                />
                              </svg>
                            </button>
                          </div>
                          <div className="premium-summary-addon-right">
                            <span className="premium-summary-addon-price">
                              {getRiderPremium(rider.id) != null
                                ? `${formatCurrency(getRiderPremium(rider.id))} / ${getDisplayFrequencyLabel(premiumFrequency)}`
                                : `— / ${getDisplayFrequencyLabel(premiumFrequency)}`}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                    <div className="premium-summary-nudge">
                      <strong>Disclaimer</strong>: Sum At Risk based on existing
                      details filled and existing insurance only, subject to
                      underwriting.
                    </div>
                  </div>
                </div>
              </div>
            )}
            <QuickPeep />
          </aside>
        </div>
      </section>

      <section className="quote-submit-section">
        {submitError && (
          <div className="error-message error-message-inline">
            {submitError}
          </div>
        )}
        {hasUserEditedQuoteRef.current && premiumError && (
          <div className="premium-banner error">
            {getRelevantPremiumErrorMessage(premiumError)}
          </div>
        )}
        <div className="bottom-banner">
          {onlineDiscount} Online{" "}
          {isSalariedOccupation ? `and ${salariedDiscount} salaried` : ""}{" "}
          discount applied on first year premium
        </div>
        <div className="quote-submit-strip">
          <div className="quote-submit-left">
            <div className="quote-submit-text">
              <svg
                width="17"
                height="17"
                viewBox="0 0 17 17"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M14.3638 5.62712C14.3638 5.4417 14.4188 5.26045 14.5218 5.10628C14.6248 4.9521 14.7713 4.83194 14.9426 4.76099C15.1139 4.69003 15.3024 4.67146 15.4842 4.70764C15.6661 4.74381 15.8331 4.8331 15.9642 4.96421C16.0954 5.09532 16.1846 5.26237 16.2208 5.44422C16.257 5.62608 16.2384 5.81458 16.1675 5.98589C16.0965 6.15719 15.9763 6.30361 15.8222 6.40662C15.668 6.50964 15.4867 6.56462 15.3013 6.56462C15.0527 6.56462 14.8142 6.46585 14.6384 6.29003C14.4626 6.11422 14.3638 5.87576 14.3638 5.62712ZM13.4263 3.75212C13.6117 3.75212 13.793 3.69714 13.9472 3.59413C14.1013 3.49111 14.2215 3.34469 14.2925 3.17339C14.3634 3.00208 14.382 2.81358 14.3458 2.63173C14.3096 2.44987 14.2204 2.28282 14.0892 2.15171C13.9581 2.0206 13.7911 1.93131 13.6092 1.89514C13.4274 1.85896 13.2389 1.87753 13.0676 1.94849C12.8963 2.01944 12.7498 2.1396 12.6468 2.29378C12.5438 2.44795 12.4888 2.6292 12.4888 2.81462C12.4888 3.06326 12.5876 3.30172 12.7634 3.47754C12.9392 3.65335 13.1777 3.75212 13.4263 3.75212ZM15.6654 8.12712C15.5003 8.1135 15.3365 8.16601 15.2101 8.2731C15.0836 8.38019 15.0049 8.5331 14.9912 8.69822C14.8793 10.0048 14.3966 11.2523 13.5998 12.2939C12.8031 13.3355 11.7254 14.1278 10.4935 14.5777C9.26174 15.0275 7.92706 15.1162 6.64656 14.8333C5.36606 14.5503 4.19301 13.9076 3.26542 12.9806C2.33784 12.0536 1.69432 10.8809 1.41058 9.60059C1.12684 8.32027 1.21468 6.98554 1.66378 5.75344C2.11287 4.52135 2.90453 3.44316 3.9456 2.64573C4.98667 1.84829 6.23385 1.36479 7.54039 1.25212C7.62247 1.24535 7.7024 1.22248 7.77564 1.18482C7.84888 1.14715 7.91398 1.09543 7.96723 1.0326C8.02048 0.96978 8.06083 0.897083 8.08598 0.818663C8.11114 0.740243 8.1206 0.657637 8.11383 0.575561C8.10705 0.493485 8.08418 0.413546 8.04652 0.340309C8.00885 0.267071 7.95713 0.20197 7.89431 0.148721C7.83148 0.0954728 7.75879 0.0551198 7.68037 0.0299665C7.60195 0.00481317 7.51934 -0.00464789 7.43726 0.00212339C5.89353 0.134909 4.41984 0.705829 3.18956 1.64771C1.95928 2.5896 1.0236 3.86327 0.492599 5.31887C-0.0384007 6.77447 -0.142629 8.35144 0.192176 9.86427C0.526981 11.3771 1.28689 12.7628 2.38251 13.8584C3.47812 14.9541 4.86385 15.714 6.37668 16.0488C7.8895 16.3836 9.46648 16.2793 10.9221 15.7483C12.3777 15.2173 13.6514 14.2817 14.5932 13.0514C15.5351 11.8211 16.106 10.3474 16.2388 8.80368C16.2459 8.72155 16.2366 8.63884 16.2116 8.5603C16.1865 8.48176 16.1462 8.40895 16.0929 8.34607C16.0396 8.28318 15.9744 8.23146 15.901 8.19389C15.8276 8.15631 15.7476 8.13362 15.6654 8.12712ZM8.11383 2.50212C9.22635 2.50212 10.3139 2.83202 11.2389 3.45011C12.1639 4.06819 12.8849 4.94669 13.3106 5.97453C13.7364 7.00236 13.8478 8.13336 13.6307 9.2245C13.4137 10.3156 12.878 11.3179 12.0913 12.1046C11.3046 12.8913 10.3024 13.427 9.21121 13.644C8.12007 13.8611 6.98907 13.7497 5.96123 13.3239C4.9334 12.8982 4.05489 12.1772 3.43681 11.2522C2.81873 10.3272 2.48883 9.23964 2.48883 8.12712C2.49048 6.63579 3.08364 5.20601 4.13818 4.15147C5.19271 3.09694 6.62249 2.50378 8.11383 2.50212ZM7.48883 8.12712C7.48883 8.29288 7.55467 8.45185 7.67188 8.56906C7.78909 8.68627 7.94807 8.75212 8.11383 8.75212H11.8638C12.0296 8.75212 12.1886 8.68627 12.3058 8.56906C12.423 8.45185 12.4888 8.29288 12.4888 8.12712C12.4888 7.96136 12.423 7.80239 12.3058 7.68518C12.1886 7.56797 12.0296 7.50212 11.8638 7.50212H8.73883V4.37712C8.73883 4.21136 8.67298 4.05239 8.55577 3.93518C8.43856 3.81797 8.27959 3.75212 8.11383 3.75212C7.94807 3.75212 7.78909 3.81797 7.67188 3.93518C7.55467 4.05239 7.48883 4.21136 7.48883 4.37712V8.12712ZM10.6138 1.87712C10.7992 1.87712 10.9805 1.82214 11.1347 1.71913C11.2888 1.61611 11.409 1.46969 11.48 1.29839C11.5509 1.12708 11.5695 0.938583 11.5333 0.756726C11.4971 0.574869 11.4079 0.407822 11.2767 0.276711C11.1456 0.145599 10.9786 0.0563109 10.7967 0.0201373C10.6149 -0.0160364 10.4264 0.00252924 10.2551 0.0734864C10.0838 0.144444 9.93734 0.264605 9.83432 0.418776C9.73131 0.572947 9.67633 0.754203 9.67633 0.939623C9.67633 1.18826 9.7751 1.42672 9.95091 1.60254C10.1267 1.77835 10.3652 1.87712 10.6138 1.87712Z"
                  fill="#FF7C1E"
                />
              </svg>
              {/* 
              <span className="quote-submit-main-text">
                Your premium from {premiumRevisionDate} will increase to{" "}
                {formatCurrency(finalPremium)} per{" "}
                {getDisplayFrequencyLabel(premiumFrequency)}
              </span>
              */}
              <span className="quote-submit-main-text">
                The premium for this policy may be higher if purchased next year
                due to age-based pricing.
              </span>
            </div>
          </div>
          <div className="quote-submit-rigth">
            <div
              className={`quote-submit-center ${
                isLeadGuidedFlowActive &&
                activeGuidedStep === GUIDED_QUOTE_STEPS.FREQUENCY
                  ? "quote-guided-target quote-guided-target--active"
                  : ""
              }`}
            >
              <div className="quote-submit-center-select">
                <label className="frequency-label" htmlFor="premiumFrequency">
                  Premium Frequency
                </label>
                <select
                  id="premiumFrequency"
                  className="frequency-select"
                  value={
                    premiumFrequency || PREMIUM_FREQUENCY_PLACEHOLDER_VALUE
                  }
                  onChange={(e) => {
                    if (
                      isLeadGuidedFlowActive &&
                      activeGuidedStep !== GUIDED_QUOTE_STEPS.FREQUENCY
                    ) {
                      return;
                    }
                    const next = e.target.value;
                    if (!isValidPremiumFrequency(next)) return;
                    markUserEditedQuote();
                    if (next === premiumFrequency) return;
                    // Same resolution as quote submit: drafts + refs (committed riderCovers can lag typing/debounce).
                    let coversForPremium = riderCovers;
                    const resolved = resolveRiderCoversForSubmit();
                    if (!resolved.error && resolved.covers) {
                      coversForPremium = resolved.covers;
                    }
                    setPremiumFrequency(next);
                    if (isLeadGuidedFlowActive) return;
                    if (
                      onPremiumFrequencyChange &&
                      effectiveCoverAmount != null &&
                      selectedPlan
                    ) {
                      onPremiumFrequencyChange(
                        next,
                        selectedPlan,
                        effectiveCoverAmount,
                        policyTermForPremium,
                        paymentTermType,
                        selectedLimitedPayTerm,
                        Array.from(selectedRiders),
                        coversForPremium,
                      );
                    }
                  }}
                >
                  {!isValidPremiumFrequency(premiumFrequency) && (
                    <option value={PREMIUM_FREQUENCY_PLACEHOLDER_VALUE}>
                      Select
                    </option>
                  )}
                  {PREMIUM_FREQUENCY_OPTIONS.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              </div>
              {/* {premiumFrequency === "Yearly" && (
                <div className="quote-submit-sub-text">
                  You save <span>{formatCurrency(premiumSavingsAmount)}</span>
                </div>
              )} */}
            </div>
            <button
              type="button"
              className="btn-submit-quote btn primary-button regular"
              onClick={handleSubmitQuote}
              disabled={isPayButtonDisabled}
            >
              {submitting
                ? "Submitting Quote..."
                : `Pay ${formatCurrency(totalPayableAmount)}`}
            </button>
          </div>
        </div>
      </section>

      {/* Premium loading overlay: skip while initial grid prefetch only (primary premium already shown) to avoid full-page → overlay flash. */}
      {shouldShowPremiumLoader && (
        <div
          className="quote-premium-loader-overlay"
          aria-live="polite"
          aria-busy="true"
        >
          <BrandLoader color="#97262A" loading={true} size={15} />
        </div>
      )}

      {/* Modals */}
      <Modal
        isOpen={showPlanIneligibleModal}
        onClose={() => setShowPlanIneligibleModal(false)}
        title="Plan Not Available"
        type="error"
      >
        <p>
          Your current selections don&apos;t meet the required product criteria
          for the Elite plan.
        </p>
        <p>{TEXT_CONTENT.PLAN_MODEL}</p>
      </Modal>
      <NeedhelpModal
        isOpen={showHelpModal}
        onClose={() => setShowHelpModal(false)}
        title="Reach us through your preferred mode"
        type="success"
      />
      <Modal
        isOpen={showPremiumRefundModal}
        onClose={() => setShowPremiumRefundModal(false)}
        title="100% Premium Refund"
        type="success"
      >
        <p>{TEXT_CONTENT.MODEL_SUCCESS}</p>
      </Modal>
      <Modal
        isOpen={showEnhancedCoverageModal}
        onClose={() => setShowEnhancedCoverageModal(false)}
        title="Enhanced Coverage Benefit Option"
        type="success"
      >
        <p>{TEXT_CONTENT.MODEL_ENHANCED}</p>
      </Modal>

      <Modal
        isOpen={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
        title="Quote Submitted Successfully!"
        type="success"
        btnTitle="Done"
      >
        <p>{TEXT_CONTENT.MODEL_SUBMIT}</p>
      </Modal>
      <Modal
        isOpen={showBiFailureModal}
        onClose={() => setShowBiFailureModal(false)}
        title="Unable to Generate Benefit Illustration"
        type="error"
        btnTitle="Okay"
      >
        <p>{biFailureMessage || BI_FAILURE_MESSAGE}</p>
      </Modal>

      {/* Edit Details Modal - shows all lead form fields with prefilled data */}
      <EditStepModal
        isOpen={showEditDetailsModal}
        onClose={() => setShowEditDetailsModal(false)}
        title="Show Details"
        onSave={() => setShowEditDetailsModal(false)}
        className="quote-edit"
        formData={editDetailsFormData}
        disableAllFields={true}
      />
    </div>
  );
};

export default TermInsuranceJourneyQuote;
